import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowRight, faBuilding, faCalendarCheck, faChartLine, faChevronDown,
  faDumbbell, faMoon, faSun, faTrophy, faUsers, faBullseye, faBell,
  faClipboardCheck, faLayerGroup, faUtensils, faCheck, faTimes, faPlus,
} from "@fortawesome/free-solid-svg-icons";
import {
  IoAdd, IoBarChartOutline, IoBusinessOutline, IoChevronBack, IoChevronForward,
  IoEyeOutline, IoFitnessOutline, IoFlashOutline, IoInformationCircleOutline,
  IoNotificationsOutline, IoPencilOutline,
} from "react-icons/io5";
import logo from "../assets/logo.png";
import { supabase } from "../supabaseClient";
import { withRequestedStripeMode } from "../stripeMode";
import { loadInstitutionBillingRows, mergeInstitutionPlansWithBillingRows } from "../services/institutionBillingPlans";
import {
  calculateInstitutionMonthlyPrice,
  calculateInstitutionOverageBreakdown,
  formatInstitutionPrice,
  institutionPriceRequiresContact,
} from "../services/institutionPricing";
import { loadInstitutionBusinessDemoConfig } from "../services/institutionDemoMode";

const SNOWS_PROAM_LINKTREE_URL = "https://linktr.ee/snowsproam";
const DISABLE_WEB_TOURNAMENT_CHECKOUT = true;

const isBusinessInstitutionPlan = (plan) => String(plan?.segment || "").toLowerCase() === "business";
const BUSINESS_DEMO_PRICING_CARD = {
  name: "Enterprise",
  segment: "Business",
  price: "Custom pricing",
  size: "Custom size",
  badge: "",
  featured: true,
  items: [
    "Member and department institutes",
    "Live web dashboards",
    "Analytics and exporting",
    "Custom branding",
    "Onboarding support",
  ],
  cta: "Book a Demo",
  isBusinessDemoCard: true,
};

const collapseBusinessDemoPlans = (plans = [], demoMode = false) => {
  if (!demoMode) return plans;
  const businessPlan = plans.find((plan) => String(plan?.tier || "").toLowerCase() === "enterprise") || plans.find(isBusinessInstitutionPlan);
  if (!businessPlan) return plans;
  return [
    ...plans.filter((plan) => !isBusinessInstitutionPlan(plan)),
    {
      ...BUSINESS_DEMO_PRICING_CARD,
      ...businessPlan,
      name: businessPlan.name || BUSINESS_DEMO_PRICING_CARD.name,
      price: businessPlan.price || BUSINESS_DEMO_PRICING_CARD.price,
      size: businessPlan.size || BUSINESS_DEMO_PRICING_CARD.size,
      cta: businessPlan.cta || BUSINESS_DEMO_PRICING_CARD.cta,
      isBusinessDemoCard: true,
    },
  ];
};

const DARK = {
  mode: "dark", accent: "#2B7FFF", accentAlt: "#00D4FF",
  accentGrad: "linear-gradient(135deg,#2B7FFF 0%,#0A1D44 100%)",
  accentGlow: "rgba(43,127,255,0.45)", accentBg: "rgba(43,127,255,0.10)",
  accentBorder: "rgba(43,127,255,0.24)", bg: "#080810", bgAlt: "#0D0D1A",
  bgCard: "#12121F", bgElevated: "#1A1A2E", border: "rgba(255,255,255,0.07)",
  borderMd: "rgba(255,255,255,0.12)", text: "#F0F0FF",
  textSec: "rgba(240,240,255,0.65)", textMuted: "rgba(240,240,255,0.36)",
  headerBg: "rgba(8,8,16,0.88)", shadowLg: "0 16px 60px rgba(0,0,0,0.6)",
};

const LIGHT = {
  mode: "light", accent: "#1A6FEB", accentAlt: "#00AACC",
  accentGrad: "linear-gradient(135deg,#1A6FEB 0%,#0A1D44 100%)",
  accentGlow: "rgba(26,111,235,0.25)", accentBg: "rgba(26,111,235,0.07)",
  accentBorder: "rgba(26,111,235,0.18)", bg: "#F0F2F8", bgAlt: "#E8EBF4",
  bgCard: "#FFFFFF", bgElevated: "#FFFFFF", border: "rgba(10,29,68,0.08)",
  borderMd: "rgba(10,29,68,0.14)", text: "#0A0E1F", textSec: "#3A4560",
  textMuted: "#7A8AAA", headerBg: "rgba(240,242,248,0.92)",
  shadowLg: "0 16px 60px rgba(10,29,68,0.12)",
};

const planItemState = (item) => {
  const unavailable = /^no\s+/i.test(item);
  const addon = /add-on/i.test(item);
  return {
    unavailable,
    addon,
    label: unavailable ? item.replace(/^no\s+/i, "") : item,
  };
};

function useTheme() {
  const [dark, setDark] = useState(() => {
    try {
      const s = localStorage.getItem("spa-theme");
      if (s) return s !== "light";
      return window.matchMedia("(prefers-color-scheme: dark)").matches;
    } catch {
      return true;
    }
  });
  const toggle = useCallback(() => {
    setDark((value) => {
      const next = !value;
      try { localStorage.setItem("spa-theme", next ? "dark" : "light"); } catch {}
      return next;
    });
  }, []);
  return [dark ? DARK : LIGHT, toggle, dark];
}

const pages = {
  wellness: {
    icon: faChartLine,
    nav: "Wellness",
    hero: {
      headline: "Move together. Compete together. Grow together.",
      sub: "",
      primary: "Compare plans",
      primaryTo: "#pricing",
    },
    proof: { title: "Relay for Life case study", stats: ["5.41M steps", "64 teams", "293 athletes", "4,120 km covered"], link: "View Relay Dashboard →", to: "/relayforlife" },
    globalProof: { title: "Worldwide ProAm movement data", stats: ["Live worldwide steps", "Athlete rankings", "Countries represented"], link: "View Steps Worldwide →", to: "https://worldwidesteps.com/" },
    sections: [
      { title: "Your Wellness Program Has No Data", items: ["No visibility into gym membership usage", "Manual spreadsheet step challenges", "Can't prove ROI to leadership"] },
      { title: "Solution", items: ["Collective step tracking across company", "Department vs department leaderboards", "Monthly wellness reports", "Free employee app (iOS + Android)"] },
      { title: "How It Works", steps: ["Employees download ProAm (free)", "Enter company code in settings", "Activity auto-syncs", "Admin sees live dashboard"] },
    ],
    pricing: [
      { name: "Friends", segment: "Social", price: "€4.99/month", monthlyProductKey: "institutions_friends_monthly", yearlyProductKey: "institutions_friends_yearly", baseAmountCents: 499, memberLimit: 5, perMember: "€1/member/month at 5 people", items: ["Up to 5 people", "Step leaderboard", "App dashboard", "No team institutes"], cta: "Create Leaderboard" },
      { name: "Crew", segment: "Social", price: "€14.99/month", monthlyProductKey: "institutions_crew_monthly", yearlyProductKey: "institutions_crew_yearly", baseAmountCents: 1499, memberLimit: 15, perMember: "€1/member/month at 15 people", items: ["Up to 15 people", "Step leaderboard", "App dashboard", "No team institutes"], cta: "Create Leaderboard" },
      { name: "Club", segment: "Social", price: "€29/month", monthlyProductKey: "institutions_club_monthly", yearlyProductKey: "institutions_club_yearly", baseAmountCents: 2900, memberLimit: 30, perMember: "€0.97/member/month at 30 people", items: ["Up to 30 people", "Step leaderboard", "App dashboard", "No team institutes"], cta: "Create Leaderboard" },
      { name: "Club Starter", segment: "Club", price: "€49/month", monthlyProductKey: "institutions_club_starter_monthly", yearlyProductKey: "institutions_club_starter_yearly", baseAmountCents: 4900, yearlyAmountCents: 49980, memberLimit: 50, perMember: "€0.98/member/month at 50 members", items: ["Up to 50 members", "Step leaderboard", "App dashboard", "Custom branding", "Team institutes", "Priority support", "Onboarding"], cta: "Start Access" },
      { name: "Club Plus", segment: "Club", price: "€99/month", monthlyProductKey: "institutions_club_plus_monthly", yearlyProductKey: "institutions_club_plus_yearly", baseAmountCents: 9900, yearlyAmountCents: 100980, memberLimit: 150, perMember: "€0.66/member/month at 150 members", items: ["Up to 150 members", "Step leaderboard", "App dashboard", "Custom branding", "Team institutes", "Priority support", "Onboarding"], cta: "Start Access" },
      { name: "Club Pro", segment: "Club", price: "€199/month", monthlyProductKey: "institutions_club_pro_monthly", yearlyProductKey: "institutions_club_pro_yearly", baseAmountCents: 19900, yearlyAmountCents: 202980, memberLimit: 300, perMember: "€0.66/member/month at 300 members", items: ["Up to 300 members", "Step leaderboard", "App dashboard", "Custom branding", "Analytics and exporting", "Team institutes", "Priority support", "Onboarding", "Live dashboard"], cta: "Start Access" },
      { name: "Club Elite", segment: "Club", price: "From €449/month", monthlyProductKey: "institutions_club_elite_monthly", yearlyProductKey: "institutions_club_elite_yearly", baseAmountCents: 44900, yearlyAmountCents: 457980, metadata: { usage_based: true, included_members: 300, overage_bands: [{ from: 301, to: null, amount_cents: 125 }] }, memberLimit: null, perMember: "300 included, then €1.25/member/month", items: ["301+ members", "Step leaderboard", "App dashboard", "Custom branding", "Analytics and exporting", "Team institutes", "Priority support", "Onboarding", "Live dashboard"], cta: "Estimate your price" },
      { name: "Team", segment: "Business", price: "€149/month", monthlyProductKey: "institutions_team_monthly", yearlyProductKey: "institutions_team_yearly", baseAmountCents: 14900, yearlyAmountCents: 151980, memberLimit: 20, perMember: "€7.45/member/month at 20 people", badge: "Full Dashboard Starts Here", featured: true, items: ["Up to 20 people", "Full web dashboard", "Custom branding", "Analytics and exporting", "Member and department institutes", "Priority support", "Onboarding", "Live dashboard"], cta: "Start Access" },
      { name: "Startup", segment: "Business", price: "€299/month", monthlyProductKey: "institutions_startup_monthly", yearlyProductKey: "institutions_startup_yearly", baseAmountCents: 29900, yearlyAmountCents: 304980, memberLimit: 50, perMember: "€5.98/member/month at 50 people", items: ["Up to 50 people", "Full web dashboard", "Custom branding", "Analytics and exporting", "Member and department institutes", "Priority support", "Onboarding", "Live dashboard"], cta: "Start Access" },
      { name: "Growth", segment: "Business", price: "€1,299/month", monthlyProductKey: "institutions_growth_monthly", yearlyProductKey: "institutions_growth_yearly", baseAmountCents: 129900, yearlyAmountCents: 1324980, memberLimit: 150, perMember: "€8.66/member/month at 150 people", items: ["Up to 150 people", "Full web dashboard", "Custom branding", "Analytics and exporting", "Member and department institutes", "Priority support", "Onboarding", "Live dashboard"], cta: "Start Access" },
      { name: "Scale", segment: "Business", price: "€1,999/month", monthlyProductKey: "institutions_scale_monthly", yearlyProductKey: "institutions_scale_yearly", baseAmountCents: 199900, yearlyAmountCents: 2038980, memberLimit: 300, perMember: "€6.66/member/month at 300 people", items: ["Up to 300 people", "Full web dashboard", "Custom branding", "Analytics and exporting", "Member and department institutes", "Priority support", "Onboarding", "Live dashboard"], cta: "Start Access" },
      { name: "Enterprise", segment: "Business", price: "Custom pricing", monthlyProductKey: "institutions_enterprise_monthly", yearlyProductKey: "institutions_enterprise_yearly", baseAmountCents: 199900, yearlyAmountCents: 2038980, metadata: { usage_based: true, requires_contact: true, public_price_label: "Custom pricing", estimate_base_amount_cents: 199900, included_members: 300, overage_bands: [{ from: 301, to: 1000, amount_cents: 300 }, { from: 1001, to: 5000, amount_cents: 200 }, { from: 5001, to: 20000, amount_cents: 100 }, { from: 20001, to: null, amount_cents: 50 }] }, badge: "Custom rollout", items: ["300 people included", "Tiered pricing after 300", "Full web dashboard", "Custom branding", "Analytics and exporting", "Member and department institutes", "Priority support", "Onboarding", "Live dashboard"], cta: "Request rollout" },
    ],
    faqs: [
      ["Who is Snows ProAm for?", "Friends can run a simple step leaderboard, clubs can manage teams and activities, and companies can run structured wellbeing across departments."],
      ["Do members need the app?", "Yes. Members use the free Snows ProAm app to join the workspace, sync activity, and follow their dashboard or leaderboard."],
      ["What can a workspace run?", "Workspaces can run step challenges, sessions, events, tournaments, communities, team or department leaderboards, and live dashboards."],
      ["Can we organise groups inside an institute?", "Yes. Social plans stay simple, club plans can use team institutes, and business plans can use member and department institutes."],
      ["Can we export data?", "Yes. Analytics and exports are available on eligible plans where reporting and proof matter."],
      ["What happens after launch or trial access?", "You choose the plan that matches your group size and dashboard needs, or pause the workspace if you are not ready to continue."],
    ],
    form: "wellness",
  },
  tournaments: {
    icon: faTrophy,
    nav: "Tournaments",
    hero: {
      headline: "Tournament day without the scramble.",
      sub: "Find open events, share one entry link, and keep brackets, scores, players, and admins aligned from first signup to final.",
      primary: "View live tournaments",
      primaryTo: "/tournaments/live",
      secondary: "Open app",
      secondaryTo: "https://linktr.ee/snowsproam",
    },
    sections: [
      { title: "The Event-Day Problem", items: ["Players do not know when they play next", "Admins update brackets in too many places", "Open team spots get lost in group chats", "Scores arrive late or without context", "Spectators see stale screenshots instead of live progress", "One organizer becomes the bottleneck"] },
      { title: "How ProAm Runs It", id: "how-it-works", steps: ["Create the tournament in the app", "Choose the format and entry price", "Share one join link with players", "Track enrolment and open team spots", "Record matches as the bracket advances", "Give Pro admins live web control"] },
      { title: "Why Organizers Upgrade", items: ["Standard unlocks stronger formats such as round robin and groups into knockout", "Pro adds the live tournament web dashboard and web admin controls", "Admin access follows the same ProAm account across app and web", "Paid entries keep price and payout rules clear", "Staff can keep brackets, queues, results, and next matches aligned"] },
    ],
    pricing: [
      { name: "Free", price: "€0", description: "For a simple first tournament.", items: ["1 active tournament", "Single elimination bracket", "Basic organizer dashboard", "12% deducted from host payouts"], note: "Players pay the exact entry price you set. The 12% is deducted from the host payout after ProAm collects paid registrations.", cta: "Start Free", priceKey: null },
      { name: "Standard Tournament", price: "€29.99 one-time", priceKey: "tournaments_standard_one_time_eur", description: "For organizers who need more bracket formats.", items: ["Single elimination bracket", "Round robin tables", "Groups into knockout", "One tournament package"], note: "Standard is for formats. It does not include the live tournament web dashboard or web admin controls.", cta: "Buy Standard" },
      { name: "Pro Tournament", price: "€39.99 one-time", badge: "Most Popular", featured: true, priceKey: "tournaments_pro_one_time_eur", description: "For serious event-day control.", items: ["Single and double elimination", "Round robin and double round robin", "Groups into knockout", "Consolation/placement matches", "Live tournament web dashboard", "Web admin controls"], note: "Best when staff need to update results, manage the tournament online, and keep spectators aligned on event day.", cta: "Buy Pro" },
    ],
    faqs: [
      ["Does Standard include the live dashboard?", "No. Standard unlocks the listed tournament formats. The live tournament web dashboard and web admin controls are included with Pro Tournament."],
      ["Who pays the 12% on Free Tournament?", "The player pays the entry price the host sets. ProAm collects the payment and deducts 12% from the host payout when paid registrations are collected."],
      ["How do I access the Pro dashboard?", "Create or unlock a Pro Tournament, open the tournament link, and sign in with the same ProAm account used to create the tournament in the app. Creator/admin access unlocks the dashboard automatically."],
      ["Do tournament plans unlock in the mobile app too?", "Yes. Tournament access is stored in Supabase entitlements, so the website checkout and mobile app read the same account access."],
      ["Are these subscriptions?", "No. Standard and Pro Tournament are one-time event packages. Repeat organizer subscriptions can still be added later if enough clubs need them."],
      ["Can I still run a basic tournament for free?", "Yes. Free access supports one active single-elimination tournament. Paid packages unlock more formats and organizer tools."],
    ],
    cta: ["Everything you need before the first match.", "Open Tournaments in App"],
  },
  events: {
    icon: faCalendarCheck,
    nav: "Events",
    hero: {
      headline: "Create Your Event In The App",
      sub: "Build the listing in Snows ProAm, choose the sport, date, location, capacity, entry fee, and promotion radius, then submit it for review.",
      primary: "Open Snows ProAm App",
      primaryTo: "https://linktr.ee/snowsproam",
      secondary: "See How It Works",
      secondaryTo: "#event-flow",
    },
    sections: [
      { title: "Who It's For", items: ["Padel tournaments", "Boxing nights", "5K and 10K runs", "Basketball leagues", "Fitness challenges", "Any organised sports event open to the public"] },
      { title: "How It Works", steps: ["Create your event listing on ProAm", "Athletes discover it on the app and web", "They register and pay through ProAm", "You get paid out minus our commission", "We promote it to relevant athletes in your area"] },
      { title: "Why ProAm", items: ["Targeted sports audience, not general public like Eventbrite", "Athletes already active on the platform", "Built-in social sharing when athletes register", "Post-event highlights reach future attendees"] },
    ],
    pricing: [],
  },
  coaching: {
    icon: faDumbbell,
    nav: "Coaching",
    hero: {
      headline: "See who trained, who missed, and who needs help.",
      sub: "For a football club, gym, academy, or personal training group: give each coach their own athletes, set session and workout targets, track calories when food matters, and see who needs help. Club Coaching also lets you use your club colours and branding so the app feels like your own setup.",
      primary: "Start Club Coaching Free Trial",
      primaryTo: "#pricing",
      secondary: "See Coach Workflow",
      secondaryTo: "#coach-workflow",
    },
    sections: [
      {
        title: "Your Coaching Week",
        id: "coach-workflow",
        kicker: "Simple jobs coaches do every week",
        cards: [
          { icon: faUsers, title: "Give Each Coach A Group", body: "If you have a football club, you can make groups for underage teams, senior squads, small groups, or one-to-one clients. Each coach can see the athletes they work with." },
          { icon: faBullseye, title: "Set The Standard", body: "Tell players how many sessions they should hit this week. Add workout targets and calorie targets when that is part of the plan." },
          { icon: faClipboardCheck, title: "Give Them The Work", body: "Pick the workouts you want players to follow. Then check who completed them, who left feedback, and who has not trained." },
          { icon: faBell, title: "Get Them Back On Track", body: "When a player misses sessions or stops logging, send a reminder. It helps coaches act early instead of waiting until the problem is obvious." },
        ],
      },
      {
        title: "What You Can Check",
        kicker: "Clear information, no guessing",
        cards: [
          { icon: faLayerGroup, title: "Training Blocks", body: "Check this week, two weeks, four weeks, custom dates, or a full club season. Use the view that matches how your team trains." },
          { icon: faUtensils, title: "Calories And Food", body: "Set daily calorie targets, see which athletes are logging food, and remind the group when nutrition matters for performance or weight goals." },
          { icon: faDumbbell, title: "Workouts And Feedback", body: "See sessions, XP, last workout, assigned workouts, and how hard the athlete said the session felt." },
          { icon: faBuilding, title: "Your Club Theme", body: "Add your club colours, logo, and branded look. Club Coaching also gives you coaches/admins, seasons, preset workout packs, and the web dashboard." },
        ],
      },
      {
        title: "Attention Status",
        kicker: "Know who to speak to first",
        cards: [
          { title: "Ahead of Plan", body: "They are doing more than the target. Keep them going or give them the next challenge." },
          { title: "On Plan", body: "They are doing what you asked. Check how they feel and keep the plan steady." },
          { title: "Building Momentum", body: "They have started, but they are not there yet. A small push can help." },
          { title: "Needs Attention", body: "They missed sessions or have gone quiet. This is who you speak to first." },
        ],
      },
    ],
    pricing: [
      { name: "Free", monthlyPrice: "€0", yearlyPrice: "€0", items: ["1 group", "Up to 2 athletes", "1 assigned workout per group", "Basic performance dashboard", "1 week time range"], cta: "Start Free" },
      { name: "Coach Starter", monthlyPrice: "€4.99/month", yearlyPrice: "€50.90/year", yearlySaving: "Save €8.98/year", monthlyPriceKey: "workout_groups_coach_starter_monthly_eur", yearlyPriceKey: "workout_groups_coach_starter_yearly_eur", items: ["Up to 2 groups", "Up to 6 athletes per group", "2 assigned workouts per group", "Workout share codes", "Basic performance dashboard", "Athlete session feedback", "Athlete + group 1W / 2W / 4W ranges"], cta: "Subscribe" },
      { name: "Coach", monthlyPrice: "€9.99/month", yearlyPrice: "€101.90/year", yearlySaving: "Save €17.98/year", badge: "Most Popular", featured: true, monthlyPriceKey: "workout_groups_coach_monthly_eur", yearlyPriceKey: "workout_groups_coach_yearly_eur", items: ["Up to 3 groups", "Up to 12 athletes per group", "5 assigned workouts per group", "Workout share codes", "Full dashboard", "Coach attention statuses", "Coach Notes", "Push reminders", "Athlete session feedback", "Rest time analysis", "1W / 2W / 4W ranges"], cta: "Subscribe" },
      { name: "Pro Coach", monthlyPrice: "€24.99/month", yearlyPrice: "€254.90/year", yearlySaving: "Save €44.98/year", monthlyPriceKey: "workout_groups_pro_coach_monthly_eur", yearlyPriceKey: "workout_groups_pro_coach_yearly_eur", items: ["Unlimited groups", "Unlimited athletes", "Unlimited assigned workouts", "Calendar sessions and attendance", "Nutrition stats and calorie reminders", "Priority support"], cta: "Subscribe" },
      { name: "Club Coaching", monthlyPrice: "€59.99/month", yearlyPrice: "€611.90/year", yearlySaving: "Save €107.98/year", badge: "1st month free online", monthlyPriceKey: "workout_groups_team_club_monthly_eur", yearlyPriceKey: "workout_groups_team_club_yearly_eur", items: ["Web club dashboard", "Club branding", "Preset workout packs", "Multiple coaches/admins", "Calendar sessions and attendance", "Season management", "Club analytics", "Online subscription includes the free trial"], cta: "Start 1-Month Free Trial" },
    ],
    faqs: [
      ["Do my athletes need the Snows ProAm app?", "Yes. Athletes join and log workouts through the free app."],
      ["Can I assign specific workouts to my group?", "Yes. Coach Starter and higher plans include workout plan assignment."],
      ["How do athletes join my group?", "They enter your join code inside Snows ProAm."],
      ["Can I have multiple groups?", "Yes, paid plans raise or remove group limits."],
      ["Is athlete data private from other coaches?", "Group access controls keep athlete data visible only to the relevant coach setup."],
      ["What's the difference between Starter, Coach, and Pro Coach?", "Starter covers small groups, workout assignment, share codes, athlete feedback, and athlete/group extra ranges. Coach adds more groups, attention statuses, notes, reminders, and rest time analysis. Pro Coach removes group and athlete limits, then adds calendar sessions, attendance, nutrition stats, and calorie reminders."],
    ],
    cta: ["Start coaching your athletes today. Free to get started.", "Start Free"],
  },
};

function Header({ C, isDark, toggleTheme, page }) {
  const navigate = useNavigate();
  return (
    <header style={{ position: "sticky", top: 0, zIndex: 50, height: 56, display: "flex", alignItems: "center", padding: "0 16px", background: C.headerBg, backdropFilter: "blur(20px) saturate(1.2)", WebkitBackdropFilter: "blur(20px) saturate(1.2)", borderBottom: `1px solid ${C.border}` }}>
      <div style={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <button type="button" className="proam-header-brand" aria-label="Go to home" onClick={() => navigate("/")} style={{ position: "relative", zIndex: 2, display: "flex", alignItems: "center", gap: 8, background: "none", border: "none", cursor: "pointer", padding: 0, flexShrink: 0 }}>
          <div style={{ width: 30, height: 30, borderRadius: 8, background: C.bgElevated, border: `1px solid ${C.border}`, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <img src={logo} alt="Snows ProAm" style={{ width: 18, height: 18, objectFit: "contain" }} />
          </div>
          <span style={{ fontFamily: "'Sora',sans-serif", fontSize: 14, fontWeight: 800, color: C.text }}>
            ProAm <span style={{ color: C.accent }}>{page.nav}</span>
          </span>
        </button>
        <div className="biz-header-center" style={{ position: "absolute", left: "50%", top: "50%", transform: "translate(-50%, -50%)", display: "flex", alignItems: "center", gap: 6, padding: "4px 10px", borderRadius: 999, background: C.accentBg, border: `1px solid ${C.accentBorder}`, fontSize: 10, fontWeight: 800, color: C.accent, letterSpacing: 0.4, textTransform: "uppercase", pointerEvents: "none" }}>
          {page.nav}
        </div>
        <div className="biz-header-actions" style={{ position: "relative", zIndex: 2, display: "flex", gap: 8 }}>
          <button type="button" className="proam-action" onClick={toggleTheme} title={isDark ? "Light mode" : "Dark mode"} aria-label={isDark ? "Switch to light mode" : "Switch to dark mode"} style={{ width: 36, height: 36, borderRadius: 10, border: `1px solid ${C.border}`, background: C.bgElevated, color: C.text, cursor: "pointer", flexShrink: 0 }}>
            <FontAwesomeIcon icon={isDark ? faSun : faMoon} />
          </button>
          <button type="button" className="proam-action" aria-label="Go back" onClick={() => navigate(-1)} style={{ minHeight: 36, fontSize: 12, fontWeight: 700, color: C.textSec, padding: "5px 10px", borderRadius: 10, border: `1px solid ${C.border}`, background: C.bgElevated, cursor: "pointer", fontFamily: "'DM Sans',sans-serif" }}>
            ← Back
          </button>
        </div>
      </div>
    </header>
  );
}

function CTAButton({ C, children, to, primary = false, emphasis = false }) {
  const navigate = useNavigate();
  const go = () => {
    if (to?.startsWith("#")) document.querySelector(to)?.scrollIntoView({ behavior: "smooth" });
    else if (to?.startsWith("http")) window.open(to, "_blank", "noopener,noreferrer");
    else navigate(to || "/");
  };
  const isStrongSecondary = !primary && emphasis;
  return (
    <button type="button" className={`proam-action ${primary ? "proam-primary-cta" : "proam-secondary-cta"}`} onClick={go} style={{ border: `1px solid ${primary ? "transparent" : isStrongSecondary ? C.accentBorder : C.borderMd}`, background: primary ? C.accentGrad : isStrongSecondary ? C.accentBg : C.bgElevated, color: primary ? "#fff" : isStrongSecondary ? C.accent : C.text, borderRadius: 13, minHeight: 46, padding: "13px 18px", fontFamily: "'Sora',sans-serif", fontWeight: 900, cursor: "pointer", boxShadow: primary ? `0 10px 34px ${C.accentGlow}` : isStrongSecondary ? `0 8px 24px ${C.mode === "dark" ? "rgba(43,127,255,.18)" : "rgba(12,105,200,.14)"}` : "none", display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8, transition: "transform .2s ease, box-shadow .2s ease, border-color .2s ease" }}>
      {children} {primary && <FontAwesomeIcon icon={faArrowRight} style={{ marginLeft: 8, fontSize: 12 }} />}
    </button>
  );
}

function PhoneMockup({ C, isDark, type }) {
  const blue = "#0C69C8";
  const navy = "#0D275B";
  const shell = isDark ? "#080810" : "#12121F";
  const screenBg = type === "coaching" ? navy : "#F0F4FA";
  const pageAccent = C.accent;
  const screenScale = type === "coaching" ? 0.84 : 0.88;

  const Pill = ({ children, active = false }) => (
    <div style={{
      padding: "4px 10px",
      borderRadius: 18,
      background: active ? navy : "#E6F0FF",
      color: active ? "#FFFFFF" : navy,
      fontSize: 9,
      fontWeight: 900,
    }}>{children}</div>
  );

  const Stat = ({ value, label }) => (
    <div style={{ flex: 1, textAlign: "center" }}>
      <div style={{ fontSize: 14, fontWeight: 900, color: blue, fontFamily: "'Sora',sans-serif" }}>{value}</div>
      <div style={{ marginTop: 2, fontSize: 8, fontWeight: 800, color: navy }}>{label}</div>
    </div>
  );

  const MiniProgress = ({ value = 70, color = blue }) => (
    <div style={{ height: 5, borderRadius: 5, background: "#E5E7EB", overflow: "hidden" }}>
      <div style={{ height: "100%", width: `${value}%`, background: color }} />
    </div>
  );

  const IconDot = ({ icon, bg = navy, color = "#FFFFFF" }) => (
    <div style={{ width: 28, height: 28, borderRadius: 12, background: bg, display: "grid", placeItems: "center", color, fontSize: 14, fontWeight: 900 }}>
      {icon}
    </div>
  );

  const MetricCard = ({ icon, value, label }) => (
    <div style={{ flex: 1, background: "#FFFFFF", borderRadius: 14, padding: "6px 5px", textAlign: "center", border: "1px solid rgba(13,39,91,0.08)" }}>
      <div style={{ color: blue, fontSize: 13, display: "grid", placeItems: "center", marginBottom: 1 }}>{icon}</div>
      <div style={{ color: navy, fontSize: 12, fontWeight: 900, fontFamily: "'Sora',sans-serif" }}>{value}</div>
      <div style={{ marginTop: 1, color: "#64748B", fontSize: 7.5, fontWeight: 900 }}>{label}</div>
    </div>
  );

  const BracketMatch = ({ title, left, right, score, active = false, winner }) => (
    <div style={{
      background: active ? "#0C69C8" : "#FFFFFF",
      color: active ? "#FFFFFF" : navy,
      border: `1px solid ${active ? "#0C69C8" : "#D7E7F8"}`,
      borderRadius: 12,
      padding: "6px 7px",
      boxShadow: active ? "0 10px 18px rgba(12,105,200,0.22)" : "none",
      minWidth: 0,
    }}>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 6, alignItems: "center", marginBottom: 4 }}>
        <span style={{ fontSize: 7.2, fontWeight: 900, opacity: active ? .72 : .54, textTransform: "uppercase" }}>{title}</span>
        <span style={{ fontSize: 7.2, fontWeight: 900 }}>{score}</span>
      </div>
      {[left, right].map((name) => (
        <div key={name} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 4, fontSize: 8.5, fontWeight: 900, lineHeight: 1.25, opacity: winner && winner !== name ? .55 : 1 }}>
          <span style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{name}</span>
          {winner === name && <span style={{ width: 5, height: 5, borderRadius: 5, background: active ? "#FFFFFF" : "#22A36F", flexShrink: 0 }} />}
        </div>
      ))}
    </div>
  );

  const tournamentScreen = (
    <div style={{ height: "100%", background: "#F0F4FA", color: navy, fontFamily: "'DM Sans',sans-serif", overflow: "hidden" }}>
      <div style={{ padding: "12px 12px 10px", background: "linear-gradient(135deg,#0C69C8,#0D275B)", color: "#FFFFFF" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
          <div>
            <div style={{ fontSize: 15, fontWeight: 900, fontFamily: "'Sora',sans-serif" }}>ProAm Open</div>
            <div style={{ marginTop: 2, color: "rgba(255,255,255,0.74)", fontSize: 9, fontWeight: 850 }}>16 player knockout</div>
          </div>
          <div style={{ background: "rgba(34,163,111,0.95)", borderRadius: 999, padding: "4px 7px", fontSize: 7.5, fontWeight: 900, letterSpacing: .4 }}>LIVE</div>
        </div>
        <div style={{ display: "flex", gap: 5, marginTop: 10 }}>
          {["Bracket", "Admin", "Join"].map((tab, index) => (
            <div key={tab} style={{ flex: 1, textAlign: "center", borderRadius: 999, padding: "5px 4px", background: index === 0 ? "#FFFFFF" : "rgba(255,255,255,0.14)", color: index === 0 ? navy : "#FFFFFF", fontSize: 8, fontWeight: 900 }}>
              {tab}
            </div>
          ))}
        </div>
      </div>

      <div style={{ padding: 9 }}>
        <div style={{ display: "flex", gap: 6 }}>
          {[
            ["16", "Joined"],
            ["€480", "Entries"],
            ["7", "Live matches"],
          ].map(([value, label]) => (
            <div key={label} style={{ flex: 1, background: "#FFFFFF", border: "1px solid #D7E7F8", borderRadius: 13, padding: "7px 5px", textAlign: "center" }}>
              <div style={{ color: blue, fontFamily: "'Sora',sans-serif", fontSize: 12, fontWeight: 900 }}>{value}</div>
              <div style={{ marginTop: 1, color: "#64748B", fontSize: 7.4, fontWeight: 900 }}>{label}</div>
            </div>
          ))}
        </div>

        <div style={{ marginTop: 7, background: "#FFFFFF", border: "1px solid #CFE2FF", borderRadius: 16, padding: 8 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 7 }}>
            <div>
              <div style={{ color: navy, fontSize: 10.5, fontWeight: 900 }}>Tournament tree</div>
              <div style={{ color: "#64748B", fontSize: 7.5, fontWeight: 850 }}>Tap a match to update scores</div>
            </div>
            <div style={{ color: blue, background: "#EAF4FF", borderRadius: 999, padding: "4px 6px", fontSize: 7.5, fontWeight: 900 }}>QF</div>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr .9fr", gap: 6, alignItems: "center" }}>
            <div style={{ display: "grid", gap: 5 }}>
              <BracketMatch title="Court 1" left="Blue Aces" right="North Club" score="11-8" winner="Blue Aces" />
              <BracketMatch title="Court 2" left="Storm" right="Harbour" score="Live" active />
              <BracketMatch title="Court 3" left="West End" right="Rivals" score="TBA" />
            </div>
            <div style={{ display: "grid", gap: 12 }}>
              <BracketMatch title="Semi" left="Blue Aces" right="Winner C2" score="Next" />
              <BracketMatch title="Semi" left="Tigers" right="Winner C3" score="14:40" />
            </div>
            <div style={{ display: "grid", gap: 7 }}>
              <div style={{ height: 24, borderLeft: "2px solid #B9DDFB", borderTop: "2px solid #B9DDFB", borderBottom: "2px solid #B9DDFB", borderRadius: "8px 0 0 8px" }} />
              <BracketMatch title="Final" left="TBD" right="TBD" score="15:20" />
              <div style={{ height: 24, borderLeft: "2px solid #B9DDFB", borderTop: "2px solid #B9DDFB", borderBottom: "2px solid #B9DDFB", borderRadius: "8px 0 0 8px" }} />
            </div>
          </div>
        </div>

        <div style={{ marginTop: 7, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 7 }}>
          <div style={{ background: navy, color: "#FFFFFF", borderRadius: 15, padding: 9 }}>
            <div style={{ fontSize: 9, fontWeight: 900, opacity: .72, textTransform: "uppercase" }}>Admin control</div>
            <div style={{ marginTop: 5, fontSize: 11.5, fontWeight: 900 }}>Storm vs Harbour</div>
            <div style={{ marginTop: 3, color: "rgba(255,255,255,0.68)", fontSize: 8.2, fontWeight: 800 }}>Court 2 - in progress</div>
            <div style={{ display: "flex", gap: 4, marginTop: 8 }}>
              {["Score", "Next"].map((label, index) => (
                <div key={label} style={{ flex: 1, textAlign: "center", background: index === 0 ? "#FFFFFF" : "rgba(255,255,255,0.13)", color: index === 0 ? navy : "#FFFFFF", borderRadius: 9, padding: "5px 3px", fontSize: 7.5, fontWeight: 900 }}>
                  {label}
                </div>
              ))}
            </div>
          </div>
          <div style={{ background: "#FFFFFF", border: "1px solid #D7E7F8", borderRadius: 15, padding: 9 }}>
            <div style={{ color: blue, fontSize: 9, fontWeight: 900, textTransform: "uppercase" }}>Join link</div>
            <div style={{ marginTop: 5, color: navy, fontSize: 11.5, fontWeight: 900 }}>3 open spots</div>
            <div style={{ marginTop: 3, color: "#64748B", fontSize: 8.2, fontWeight: 800 }}>Players join from the app</div>
            <div style={{ marginTop: 8, display: "grid", gap: 4 }}>
              {["Invite admin", "Share tournament"].map((label) => (
                <div key={label} style={{ border: "1px solid #CFE2FF", borderRadius: 9, padding: "4px 5px", color: navy, fontSize: 7.5, fontWeight: 900 }}>
                  {label}
                </div>
              ))}
            </div>
          </div>
        </div>

        <div style={{ marginTop: 7, background: "#FFFFFF", border: "1px solid #D7E7F8", borderRadius: 15, padding: 8 }}>
          <div style={{ display: "flex", justifyContent: "space-between", gap: 8, alignItems: "center" }}>
            <div>
              <div style={{ color: navy, fontSize: 10, fontWeight: 900 }}>Next match queue</div>
              <div style={{ marginTop: 2, color: "#64748B", fontSize: 7.8, fontWeight: 800 }}>Auto-built from results and schedule</div>
            </div>
            <div style={{ color: "#166534", background: "#ECFDF3", borderRadius: 999, padding: "4px 6px", fontSize: 7.3, fontWeight: 900 }}>Synced</div>
          </div>
        </div>
      </div>
    </div>
  );

  const coachingScreen = (
    <div style={{ height: "100%", background: screenBg, color: "#FFFFFF", fontFamily: "'DM Sans',sans-serif", overflow: "hidden" }}>
      <div style={{ padding: "11px 12px 8px", background: "linear-gradient(180deg,#0C69C8 0%,rgba(12,105,200,0) 100%)" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div style={{ width: 28, height: 28, borderRadius: 18, background: "rgba(255,255,255,0.15)", display: "grid", placeItems: "center", fontSize: 16 }}><IoChevronBack /></div>
          <div style={{ textAlign: "center" }}>
            <div style={{ fontSize: 14, fontWeight: 900 }}>AFC Under 16s</div>
            <div style={{ marginTop: 2, fontSize: 8, fontWeight: 900, color: "rgba(255,255,255,0.7)", letterSpacing: .8 }}>CLUB COACHING</div>
          </div>
          <div style={{ width: 28, height: 28, borderRadius: 18, background: "rgba(255,255,255,0.15)", display: "grid", placeItems: "center", fontSize: 17 }}><IoAdd /></div>
        </div>
      </div>

      <div style={{ padding: "0 10px 10px" }}>
        <div style={{ display: "flex", justifyContent: "center", gap: 6, alignItems: "center", marginBottom: 6 }}>
          <Pill>1W</Pill><Pill active>2W</Pill><Pill>4W</Pill>
          <IconDot icon={<IoEyeOutline />} />
          <IconDot icon={<IoInformationCircleOutline />} bg="#EEF6FF" color={navy} />
        </div>

        <div style={{ background: "#FFFFFF", borderRadius: 16, padding: 8, border: "1px solid #CFE2FF", display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
          <div style={{ display: "flex", alignItems: "center", minWidth: 0 }}>
            <div style={{ width: 34, height: 34, borderRadius: 17, background: "#E6F0FF", display: "grid", placeItems: "center", marginRight: 8, color: blue, fontWeight: 900 }}><IoBusinessOutline /></div>
            <div style={{ minWidth: 0 }}>
              <div style={{ color: navy, fontSize: 11.5, fontWeight: 900 }}>Club Coaching Dashboard</div>
              <div style={{ color: "#64748B", fontSize: 9, fontWeight: 800, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>Coaches, seasons, branding</div>
            </div>
          </div>
          <div style={{ color: navy, fontSize: 14, fontWeight: 900 }}><IoChevronForward /></div>
        </div>

        <div style={{ background: "#FFFFFF", borderRadius: 16, padding: 8, border: "1px solid #E0E7EF" }}>
          <div style={{ display: "flex", alignItems: "center" }}>
            <Stat value="24" label="Players" />
            <div style={{ width: 1, height: 28, background: "#E0E7EF" }} />
            <Stat value="18" label="Active" />
            <div style={{ width: 1, height: 28, background: "#E0E7EF" }} />
            <Stat value="3" label="Target" />
          </div>
        </div>

        <div style={{ display: "flex", gap: 6, marginTop: 6 }}>
          <MetricCard icon={<IoFlashOutline />} value="14,880" label="XP" />
          <MetricCard icon={<IoBarChartOutline />} value="620" label="Avg" />
          <MetricCard icon={<IoFitnessOutline />} value="42" label="Workouts" />
        </div>

        <div style={{ marginTop: 6, background: blue, borderRadius: 15, padding: 8 }}>
          <div style={{ display: "flex", justifyContent: "space-between", fontSize: 10, fontWeight: 900 }}>
            <span>2 Week Target</span><span>14880/18000</span>
          </div>
          <div style={{ marginTop: 5, height: 5, borderRadius: 5, background: "rgba(255,255,255,0.3)", overflow: "hidden" }}>
            <div style={{ width: "82%", height: "100%", background: "#FFFFFF" }} />
          </div>
        </div>

        <div style={{ marginTop: 6, background: "#FFFFFF", borderRadius: 15, padding: 8, border: "1px solid rgba(13,39,91,0.08)" }}>
          <div style={{ display: "flex", justifyContent: "space-between", gap: 8, alignItems: "center" }}>
            <div>
              <div style={{ color: navy, fontSize: 11, fontWeight: 900 }}>Group calorie target</div>
              <div style={{ color: "#64748B", fontSize: 9, fontWeight: 800, marginTop: 2 }}>2,600 kcal/day · food reminders</div>
            </div>
            <div style={{ display: "flex", gap: 5 }}>
              <IconDot icon={<IoPencilOutline />} bg="#E6F0FF" color={blue} />
              <IconDot icon={<IoNotificationsOutline />} bg="#FFF7ED" color="#D97706" />
            </div>
          </div>
        </div>

        <div style={{ display: "grid", gap: 6, marginTop: 7 }}>
          {[
            ["Jamie Walsh", "Ahead of Plan", "1,740 XP", "5 / 6", "2,480 kcal", 94, "#9A3412", "#FFF7ED"],
            ["Noah Kelly", "On Plan", "1,120 XP", "3 / 6", "2,610 kcal", 78, "#166534", "#ECFDF3"],
            ["Mia Byrne", "Needs Attention", "360 XP", "1 / 6", "No food log", 28, "#991B1B", "#FEF2F2"],
          ].map(([name, status, xp, sessions, calories, progress, statusColor, statusBg]) => (
            <div key={name} style={{ display: "flex", gap: 8, alignItems: "center", background: "#FFFFFF", borderRadius: 15, padding: 7, color: navy, boxShadow: "0 8px 20px rgba(0,0,0,0.06)" }}>
              <div style={{ width: 28, height: 28, borderRadius: 10, background: "linear-gradient(135deg,#CFE2FF,#FFFFFF)", border: "1px solid #E0E7EF" }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: "flex", justifyContent: "space-between", gap: 6, alignItems: "center" }}>
                  <div style={{ fontSize: 10.5, fontWeight: 900, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{name}</div>
                  <div style={{ color: statusColor, background: statusBg, borderRadius: 999, padding: "3px 5px", fontSize: 6.8, fontWeight: 900, whiteSpace: "nowrap" }}>{status}</div>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", marginTop: 3, fontSize: 9, fontWeight: 900 }}>
                  <span style={{ color: blue }}>{xp}</span><span>{sessions}</span>
                </div>
                <div style={{ marginTop: 2, color: "#92400E", fontSize: 8.5, fontWeight: 900 }}>{calories}</div>
                <div style={{ marginTop: 4 }}><MiniProgress value={progress} /></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const businessScreens = {
    events: {
      title: "Events",
      subtitle: "Live registration",
      metrics: [["18", "Signups"], ["€540", "Revenue"], ["6", "Spots"]],
      rows: [["Padel Open", "Featured", "12 spots"], ["5K Night Run", "Paid", "44 joined"], ["Boxing Clinic", "Live", "€25 entry"]],
    },
    wellness: {
      title: "Wellness",
      subtitle: "Company challenge",
      metrics: [["5.4M", "Steps"], ["64", "Teams"], ["293", "Athletes"]],
      rows: [["Sales", "1.2M steps", "1st"], ["Operations", "980k steps", "2nd"], ["Finance", "760k steps", "3rd"]],
    },
    tournaments: {
      title: "Tournament",
      subtitle: "Bracket and entries",
      metrics: [["32", "Entries"], ["€960", "Fees"], ["8", "Matches"]],
      rows: [["Quarter Final", "Blue vs Red", "Live"], ["Court 2", "Next match", "14:30"], ["Highlights", "3 clips", "Ready"]],
    },
  };

  const screen = businessScreens[type] || businessScreens.wellness;
  const simpleScreen = (
    <div style={{ height: "100%", background: "#F0F4FA", color: navy, fontFamily: "'DM Sans',sans-serif", overflow: "hidden" }}>
      <div style={{ padding: "15px 12px", background: `linear-gradient(135deg,${pageAccent},#0D275B)`, color: "#FFFFFF" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div>
            <div style={{ fontSize: 16, fontWeight: 900, fontFamily: "'Sora',sans-serif" }}>{screen.title}</div>
            <div style={{ marginTop: 3, color: "rgba(255,255,255,0.74)", fontSize: 10, fontWeight: 800 }}>{screen.subtitle}</div>
          </div>
          <div style={{ width: 30, height: 30, borderRadius: 15, background: "rgba(255,255,255,0.16)", display: "grid", placeItems: "center", fontSize: 16 }}><IoAdd /></div>
        </div>
      </div>
      <div style={{ padding: 10 }}>
        <div style={{ background: "#FFFFFF", borderRadius: 16, border: "1px solid #E0E7EF", padding: 10, display: "flex" }}>
          {screen.metrics.map(([value, label], index) => (
            <React.Fragment key={label}>
              <Stat value={value} label={label} />
              {index < screen.metrics.length - 1 && <div style={{ width: 1, height: 30, background: "#E0E7EF" }} />}
            </React.Fragment>
          ))}
        </div>
        <div style={{ marginTop: 10, display: "grid", gap: 8 }}>
          {screen.rows.map(([title, meta, tag], index) => (
            <div key={title} style={{ background: "#FFFFFF", borderRadius: 16, border: "1px solid #E0E7EF", padding: 10 }}>
              <div style={{ display: "flex", justifyContent: "space-between", gap: 8, alignItems: "center" }}>
                <div>
                  <div style={{ fontSize: 11.5, fontWeight: 900 }}>{title}</div>
                  <div style={{ marginTop: 3, fontSize: 9.5, color: "#64748B", fontWeight: 800 }}>{meta}</div>
                </div>
                <div style={{ color: pageAccent, background: "rgba(12,105,200,0.09)", borderRadius: 999, padding: "4px 7px", fontSize: 8, fontWeight: 900 }}>{tag}</div>
              </div>
              <div style={{ marginTop: 8 }}><MiniProgress value={[82, 64, 48][index] || 55} color={pageAccent} /></div>
            </div>
          ))}
        </div>
        <div style={{ marginTop: 10, background: navy, color: "#FFFFFF", borderRadius: 16, padding: 11 }}>
          <div style={{ fontSize: 11, fontWeight: 900 }}>Live dashboard</div>
          <div style={{ marginTop: 4, color: "rgba(255,255,255,0.72)", fontSize: 9.5, fontWeight: 700 }}>Payments, activity, and reporting in one view.</div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="fu phone-wrap" style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: 470, position: "relative" }}>
      <div style={{
        width: 232,
        height: 464,
        borderRadius: 42,
        flexShrink: 0,
        background: shell,
        border: `7px solid ${isDark ? "rgba(255,255,255,0.14)" : "rgba(255,255,255,0.22)"}`,
        overflow: "hidden",
        position: "relative",
        boxShadow: isDark ? "0 40px 80px rgba(0,0,0,0.55), 0 0 0 1px rgba(255,255,255,0.04)" : "0 34px 80px rgba(10,29,68,0.22)",
        transform: type === "coaching" ? "rotate(1.5deg)" : "rotate(-2deg)",
      }}>
        <div style={{ width: 80, height: 22, borderRadius: 11, background: "#000", margin: "8px auto", position: "relative", zIndex: 2 }} />
        <div style={{ padding: "0 8px", height: "calc(100% - 38px)", overflow: "hidden" }}>
          <div style={{
            width: `${100 / screenScale}%`,
            height: `${100 / screenScale}%`,
            transform: `scale(${screenScale})`,
            transformOrigin: "top left",
          }}>
            {type === "coaching" ? coachingScreen : type === "tournaments" ? tournamentScreen : simpleScreen}
          </div>
        </div>
      </div>
    </div>
  );
}

function CardGrid({ C, section }) {
  const values = section.steps || section.items || [];
  return (
    <section id={section.id} className="fu" style={{ maxWidth: 1140, margin: "0 auto", padding: "34px 18px" }}>
      <h2 style={{ fontFamily: "'Sora',sans-serif", fontSize: "clamp(26px,4vw,42px)", margin: "0 0 18px" }}>{section.title}</h2>
      <div className="biz-grid" style={{ display: "grid", gridTemplateColumns: "repeat(3,minmax(0,1fr))", gap: 14 }}>
        {values.map((item, i) => (
          <div key={item} style={{ background: C.bgCard, border: `1px solid ${C.border}`, borderRadius: 16, padding: 18, minHeight: 112 }}>
            {section.steps && <div style={{ color: C.accent, fontFamily: "'Sora',sans-serif", fontSize: 28, fontWeight: 900, marginBottom: 8 }}>{i + 1}</div>}
            <p style={{ margin: 0, color: C.textSec, lineHeight: 1.55, fontWeight: 600 }}>{item}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

function CoachingShowcase({ C, sections }) {
  return (
    <section className="fu" style={{ maxWidth: 1140, margin: "0 auto", padding: "30px 18px 10px" }}>
      {sections.map((section, sectionIndex) => (
        <div key={section.title} id={section.id} style={{ marginBottom: 28 }}>
          <div style={{ display: "flex", justifyContent: "space-between", gap: 18, alignItems: "flex-end", marginBottom: 14, flexWrap: "wrap" }}>
            <div>
              {section.kicker && (
                <div style={{ color: C.accent, fontSize: 11, fontWeight: 900, textTransform: "uppercase", letterSpacing: .5, marginBottom: 7 }}>
                  {section.kicker}
                </div>
              )}
              <h2 style={{ fontFamily: "'Sora',sans-serif", fontSize: "clamp(26px,4vw,42px)", margin: 0 }}>{section.title}</h2>
            </div>
            {sectionIndex === 0 && (
              <div style={{ background: C.accentBg, border: `1px solid ${C.accentBorder}`, color: C.accent, borderRadius: 999, padding: "8px 12px", fontSize: 12, fontWeight: 900 }}>
                Built around the new app UI
              </div>
            )}
          </div>

          <div className="biz-grid" style={{ display: "grid", gridTemplateColumns: "repeat(4,minmax(0,1fr))", gap: 12 }}>
            {section.cards.map((card, index) => (
              <div key={card.title} style={{
                background: sectionIndex === 2 ? C.bgElevated : C.bgCard,
                border: `1px solid ${sectionIndex === 2 && index === 3 ? C.accentBorder : C.border}`,
                borderRadius: 16,
                padding: 16,
                minHeight: 170,
                boxShadow: sectionIndex === 0 && index === 1 ? C.shadowLg : "none",
              }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
                  <div style={{
                    width: 34,
                    height: 34,
                    borderRadius: 12,
                    background: C.accentBg,
                    border: `1px solid ${C.accentBorder}`,
                    display: "grid",
                    placeItems: "center",
                    color: C.accent,
                    fontWeight: 900,
                    flexShrink: 0,
                  }}>
                    {card.icon ? <FontAwesomeIcon icon={card.icon} /> : index + 1}
                  </div>
                  <h3 style={{ margin: 0, color: C.text, fontSize: 14, lineHeight: 1.25, fontFamily: "'Sora',sans-serif", fontWeight: 900 }}>
                    {card.title}
                  </h3>
                </div>
                <p style={{ margin: 0, color: C.textSec, lineHeight: 1.55, fontSize: 13.5, fontWeight: 650 }}>{card.body}</p>
              </div>
            ))}
          </div>
        </div>
      ))}
    </section>
  );
}

function CoachingJourneyStrip({ C }) {
  const navigate = useNavigate();
  const steps = [
    { icon: faUsers, title: "Start With A Group", body: "Create one free group, invite athletes, and see whether the workflow fits your coaching setup." },
    { icon: faBullseye, title: "Upgrade When Limits Matter", body: "Move to Coach or Pro Coach when you need more groups, athletes, workout assignments, reminders, or nutrition tracking." },
    { icon: faBuilding, title: "Run The Club Layer", body: "Use Club Coaching when staff, seasons, branded dashboards, and club-wide analytics become the operating system." },
  ];
  return (
    <section className="fu proam-modern-surface" style={{ maxWidth: 1180, margin: "0 auto", padding: "18px 18px 34px" }}>
      <div
        className="biz-grid proam-flow-grid proam-motion-card"
        style={{
          background: C.mode === "dark"
            ? "radial-gradient(circle at 18% 18%, rgba(43,127,255,0.36), transparent 34%), linear-gradient(135deg,#101423,#080810)"
            : "radial-gradient(circle at 18% 18%, rgba(26,111,235,0.20), transparent 34%), linear-gradient(135deg,#FFFFFF,#EEF5FF)",
          border: `1px solid ${C.accentBorder}`,
          borderRadius: 26,
          padding: "clamp(20px,3vw,30px)",
          display: "grid",
          gridTemplateColumns: "minmax(0,0.85fr) minmax(320px,1.15fr)",
          gap: 22,
          alignItems: "stretch",
          boxShadow: C.shadowLg,
        }}
      >
        <div style={{ display: "flex", flexDirection: "column", justifyContent: "space-between", minHeight: 320 }}>
          <div>
            <div style={{ display: "inline-flex", background: C.accentBg, border: `1px solid ${C.accentBorder}`, color: C.accent, borderRadius: 999, padding: "7px 11px", fontSize: 11, fontWeight: 900, textTransform: "uppercase", letterSpacing: 0.6 }}>
              Coaching workflow
            </div>
            <h2 style={{ margin: "14px 0 10px", color: C.text, fontFamily: "'Sora',sans-serif", fontSize: "clamp(28px,4.4vw,48px)", lineHeight: 0.98 }}>
              The path from one group to a club coaching system.
            </h2>
            <p style={{ margin: 0, color: C.textSec, fontSize: 15, fontWeight: 650, lineHeight: 1.6 }}>
              Coaches should not have to choose between a simple invite flow and a serious club dashboard. ProAm starts lightweight, then expands only when the operation needs it.
            </p>
          </div>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginTop: 18 }}>
            <button
              type="button"
              className="proam-action"
              onClick={() => navigate("/profile?tab=workouts")}
              style={{ border: "none", background: C.accentGrad, color: "#fff", borderRadius: 13, padding: "12px 15px", fontFamily: "'Sora',sans-serif", fontWeight: 900, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 8 }}
            >
              <span>Start In Profile</span> <FontAwesomeIcon icon={faArrowRight} style={{ flexShrink: 0 }} />
            </button>
            <button
              type="button"
              className="proam-action"
              onClick={() => navigate("/coaching/dashboard")}
              style={{ border: `1px solid ${C.accentBorder}`, background: C.bgCard, color: C.accent, borderRadius: 13, padding: "12px 15px", fontFamily: "'Sora',sans-serif", fontWeight: 900, cursor: "pointer" }}
            >
              <span>Open Club Dashboard</span>
            </button>
          </div>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(3,minmax(0,1fr))", gap: 12 }} className="biz-grid proam-stagger">
          {steps.map((step, index) => (
            <div key={step.title} className="proam-motion-card" style={{ background: C.bgCard, border: `1px solid ${index === 2 ? C.accentBorder : C.border}`, borderRadius: 18, padding: 16, display: "flex", flexDirection: "column", minHeight: 260, boxShadow: index === 2 ? C.shadowLg : "none" }}>
              <div style={{ width: 46, height: 46, borderRadius: 15, display: "grid", placeItems: "center", background: index === 2 ? C.accentGrad : C.accentBg, border: `1px solid ${C.accentBorder}`, color: index === 2 ? "#fff" : C.accent, fontWeight: 900, marginBottom: 14 }}>
                <FontAwesomeIcon icon={step.icon} />
              </div>
              <div style={{ color: C.textMuted, fontSize: 10, fontWeight: 900, textTransform: "uppercase", letterSpacing: 0.5 }}>Step {index + 1}</div>
              <h3 style={{ margin: "5px 0 8px", color: C.text, fontSize: 18, lineHeight: 1.16, fontFamily: "'Sora',sans-serif" }}>{step.title}</h3>
              <p style={{ margin: 0, color: C.textSec, fontSize: 13, fontWeight: 650, lineHeight: 1.55 }}>{step.body}</p>
              <div style={{ marginTop: "auto", paddingTop: 16, color: index === 2 ? C.accent : C.textMuted, fontSize: 12, fontWeight: 900 }}>
                {index === 0 ? "Free starting point" : index === 1 ? "Paid when needed" : "Club operating layer"}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function TournamentOpsStrip({ C }) {
  const controls = [
    ["Bracket", "Build the route to finals"],
    ["Queue", "Tell players what is next"],
    ["Scores", "Record results once"],
    ["Admins", "Keep staff aligned"],
  ];
  const liveItems = [
    ["Now", "Court 2", "Storm vs Harbour", "#22A36F"],
    ["Next", "Semi Final", "Blue Aces waiting", C.accent],
    ["Later", "Final", "Projected 15:20", "#F59E0B"],
  ];
  return (
    <section className="fu proam-modern-surface" aria-labelledby="tournament-control-title" style={{ maxWidth: 1220, margin: "0 auto", padding: "10px 18px 36px" }}>
      <div style={{
        display: "grid",
        gridTemplateColumns: "minmax(0,.78fr) minmax(330px,1.22fr)",
        gap: 20,
        alignItems: "stretch",
        background: C.mode === "dark" ? "linear-gradient(135deg,#101423,#080810)" : "linear-gradient(135deg,#FFFFFF,#EEF5FF)",
        border: `1px solid ${C.accentBorder}`,
        borderRadius: 24,
        padding: "clamp(18px,3vw,28px)",
        boxShadow: C.shadowLg,
      }} className="biz-grid">
        <div style={{ display: "flex", flexDirection: "column", justifyContent: "space-between", gap: 18 }}>
          <div>
            <div style={{ color: C.accent, fontSize: 11, fontWeight: 950, textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 9 }}>Live control</div>
            <h2 id="tournament-control-title" style={{ margin: 0, color: C.text, fontFamily: "'Sora',sans-serif", fontSize: "clamp(28px,4.4vw,50px)", lineHeight: 1.02 }}>
              One live room when the tournament starts.
            </h2>
            <p style={{ margin: "12px 0 0", color: C.textSec, fontSize: 15, fontWeight: 750, lineHeight: 1.5 }}>
              Staff should not be answering the same question all day. Pro keeps brackets, match queue, scores, and admin controls in one web view.
            </p>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(2,minmax(0,1fr))", gap: 8 }}>
            {controls.map(([label, body], index) => (
              <div key={label} className="tournament-control-chip" style={{ border: `1px solid ${index === 0 ? C.accentBorder : C.border}`, borderRadius: 14, padding: "11px 10px", background: index === 0 ? C.accentBg : C.bgCard }}>
                <div style={{ color: index === 0 ? C.accent : C.text, fontSize: 12, fontWeight: 950 }}>{label}</div>
                <div style={{ color: C.textSec, fontSize: 10.5, fontWeight: 800, lineHeight: 1.3, marginTop: 4 }}>{body}</div>
              </div>
            ))}
          </div>
        </div>
        <div className="tournament-live-board" style={{ position: "relative", overflow: "hidden", border: `1px solid ${C.border}`, background: C.bgCard, borderRadius: 22, padding: 16 }}>
          <div className="tournament-panel-scan" aria-hidden="true" />
          <div style={{ position: "relative", display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 10 }} className="biz-grid">
            {[
              ["Next match", "Court 2", "Storm vs Harbour", C.accent],
              ["Status", "Live", "Scores updating", "#22A36F"],
            ].map(([label, title, body, color], index) => (
              <div key={label} style={{ border: `1px solid ${index === 0 ? C.accentBorder : "rgba(34,163,111,.26)"}`, background: index === 0 ? C.accentBg : "rgba(34,163,111,.10)", borderRadius: 16, padding: 14 }}>
                <div style={{ color, fontSize: 11, fontWeight: 950, textTransform: "uppercase", letterSpacing: .5 }}>{label}</div>
                <div style={{ marginTop: 7, color: C.text, fontFamily: "'Sora',sans-serif", fontSize: 22, fontWeight: 950 }}>{title}</div>
                <div style={{ marginTop: 4, color: C.textSec, fontSize: 13, fontWeight: 850 }}>{body}</div>
              </div>
            ))}
          </div>
          <div style={{ position: "relative", display: "grid", gap: 8, marginBottom: 10 }}>
            {liveItems.map(([label, title, body, color]) => (
              <div key={`${label}-${title}`} className="tournament-live-item" style={{ display: "grid", gridTemplateColumns: "54px minmax(0,1fr) 54px", gap: 10, alignItems: "center", border: `1px solid ${C.border}`, background: C.mode === "dark" ? "rgba(255,255,255,.035)" : "#FFFFFF", borderRadius: 15, padding: "10px 11px" }}>
                <div style={{ color, fontSize: 11, fontWeight: 950, textTransform: "uppercase" }}>{label}</div>
                <div style={{ minWidth: 0 }}>
                  <div style={{ color: C.text, fontSize: 14, fontWeight: 950, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{title}</div>
                  <div style={{ color: C.textSec, marginTop: 2, fontSize: 12, fontWeight: 800, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{body}</div>
                </div>
                <div aria-hidden="true" style={{ height: 8, borderRadius: 999, background: `linear-gradient(90deg,${color},${C.accentAlt})` }} />
              </div>
            ))}
          </div>
          <div style={{ position: "relative", display: "grid", gridTemplateColumns: "repeat(3,minmax(0,1fr))", gap: 8 }}>
            {["Quarter", "Semi", "Final"].map((round, index) => (
              <div key={round} style={{ border: `1px solid ${index === 1 ? C.accentBorder : C.border}`, background: index === 1 ? C.accentBg : C.mode === "dark" ? "rgba(255,255,255,.035)" : "#FFFFFF", borderRadius: 15, padding: 12, minHeight: 98 }}>
                <div style={{ color: index === 1 ? C.accent : C.textSec, fontSize: 11, fontWeight: 950 }}>{round}</div>
                <div style={{ marginTop: 12, height: 9, borderRadius: 999, background: index === 0 ? C.accent : C.borderMd }} />
                <div style={{ marginTop: 8, height: 9, borderRadius: 999, background: index === 2 ? C.borderMd : C.accent }} />
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function TournamentHero({ C, page }) {
  const proof = [
    ["01", "Players", "Know the event, spot, and next match."],
    ["02", "Clubs", "Fill brackets without chasing messages."],
    ["03", "Organizers", "Run scores, queues, and admins live."],
  ];
  return (
    <section className="tournament-cinema-hero" style={{ position: "relative", overflow: "hidden", padding: "clamp(48px,7vw,96px) 18px clamp(32px,5vw,62px)", background: C.mode === "dark" ? "linear-gradient(180deg,#07101F 0%,#090910 58%,#080810 100%)" : "linear-gradient(180deg,#F5FAFF 0%,#EDF5FF 58%,#F0F2F8 100%)" }}>
      <div className="tournament-cinema-bg" aria-hidden="true" />
      <div className="tournament-motion-field" aria-hidden="true">
        <span className="tournament-trace tournament-trace-one" />
        <span className="tournament-trace tournament-trace-two" />
      </div>
      <div className="tournament-cinema-grid fu" style={{ position: "relative", zIndex: 1, maxWidth: 1220, margin: "0 auto", display: "grid", gridTemplateColumns: "minmax(0,.95fr) minmax(360px,1.05fr)", gap: "clamp(28px,5vw,62px)", alignItems: "center" }}>
        <div className="tournament-hero-copy">
          <div style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: "7px 11px", borderRadius: 999, background: C.accentBg, border: `1px solid ${C.accentBorder}`, color: C.accent, fontSize: 11, fontWeight: 950, textTransform: "uppercase", letterSpacing: .6, marginBottom: 18 }}>
            <FontAwesomeIcon icon={faTrophy} aria-hidden="true" /> ProAm Tournaments
          </div>
          <h1 className="tournament-hero-title" style={{ color: C.text, fontFamily: "'Sora',sans-serif", fontSize: "clamp(42px,6.6vw,86px)", fontWeight: 950, lineHeight: .92, letterSpacing: 0, margin: "0 0 18px", maxWidth: 820 }}>
            Tournament day without the <span className="tournament-gradient-word">scramble</span>.
          </h1>
          <p style={{ color: C.textSec, fontSize: "clamp(16px,2vw,21px)", lineHeight: 1.48, maxWidth: 700, margin: "0 0 24px", fontWeight: 750 }}>
            {page.hero.sub}
          </p>
          <div className="tournament-hero-actions" style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "center" }}>
            <CTAButton C={C} to={page.hero.primaryTo} primary>{page.hero.primary}</CTAButton>
            <CTAButton C={C} to={page.hero.secondaryTo}>{page.hero.secondary}</CTAButton>
          </div>
          <div className="tournament-hero-proof" style={{ display: "grid", gridTemplateColumns: "repeat(3,minmax(0,1fr))", gap: 9, marginTop: 24, maxWidth: 720 }}>
            {proof.map(([number, title, body], index) => (
              <div key={title} style={{ border: `1px solid ${index === 2 ? C.accentBorder : C.border}`, background: index === 2 ? C.accentBg : C.mode === "dark" ? "rgba(255,255,255,0.045)" : "rgba(255,255,255,0.72)", borderRadius: 15, padding: "12px 10px" }}>
                <div style={{ color: C.accent, fontFamily: "'Sora',sans-serif", fontSize: 18, fontWeight: 950 }}>{number}</div>
                <div style={{ color: C.text, fontSize: 12, fontWeight: 950, marginTop: 4 }}>{title}</div>
                <div style={{ color: C.textSec, fontSize: 11, fontWeight: 800, lineHeight: 1.35, marginTop: 4 }}>{body}</div>
              </div>
            ))}
          </div>
        </div>
        <TournamentHeroVisual C={C} />
      </div>
    </section>
  );
}

function TournamentHeroVisual({ C }) {
  const players = [
    ["p1", "Ann", 1],
    ["p8", "Kai", 8],
    ["p4", "Noa", 4],
    ["p5", "Mia", 5],
    ["p2", "Lea", 2],
    ["p7", "Oli", 7],
    ["p3", "Ira", 3],
    ["p6", "Finn", 6],
  ];
  const resultMap = new Map([
    ["0-0", { winner: "p1", score: "11-7" }],
    ["0-1", { winner: "p4", score: "11-9" }],
    ["0-2", { winner: "p2", score: "10-8" }],
    ["0-3", { winner: "p3", score: "11-6" }],
    ["1-0", { winner: "p4", score: "12-10" }],
    ["1-1", { winner: "p3", score: "11-8" }],
    ["2-0", { winner: "p3", score: "13-11" }],
  ]);
  const playerById = new Map(players.map(([id, name, seed]) => [id, { id, name, seed }]));
  const firstRound = players.map(([id]) => playerById.get(id));
  const buildRound = (sourceRound, roundIndex) => {
    const next = [];
    for (let i = 0; i < sourceRound.length; i += 2) {
      const result = resultMap.get(`${roundIndex}-${i / 2}`);
      next.push(result ? playerById.get(result.winner) : null);
    }
    return next;
  };
  const semiRound = buildRound(firstRound, 0);
  const finalRound = buildRound(semiRound, 1);
  const winnerRound = buildRound(finalRound, 2);
  const treeRounds = [
    { label: "Quarter", items: firstRound, x: 20, y: [28, 74, 120, 166, 212, 258, 304, 350] },
    { label: "Semi", items: semiRound, x: 178, y: [51, 143, 235, 327] },
    { label: "Final", items: finalRound, x: 334, y: [97, 281] },
    { label: "Winner", items: winnerRound, x: 474, y: [189] },
  ];
  const winnerIds = new Set([...semiRound, ...finalRound, ...winnerRound].filter(Boolean).map((player) => player.id));
  const connectorPaths = [
    "M62 49 C120 49 120 72 178 72",
    "M62 95 C120 95 120 72 178 72",
    "M62 141 C120 141 120 164 178 164",
    "M62 187 C120 187 120 164 178 164",
    "M62 233 C120 233 120 256 178 256",
    "M62 279 C120 279 120 256 178 256",
    "M62 325 C120 325 120 348 178 348",
    "M62 371 C120 371 120 348 178 348",
    "M220 72 C286 72 286 118 334 118",
    "M220 164 C286 164 286 118 334 118",
    "M220 256 C286 256 286 302 334 302",
    "M220 348 C286 348 286 302 334 302",
    "M376 118 C432 118 432 210 474 210",
    "M376 302 C432 302 432 210 474 210",
  ];

  return (
    <div className="tournament-hero-visual tournament-real-preview" aria-label="Animated ProAm tournament bracket and admin scoring preview" style={{ position: "relative", minHeight: 560 }}>
      <div className="tournament-real-live" style={{ position: "relative", borderRadius: 30, overflow: "hidden", boxShadow: C.shadowLg, background: "linear-gradient(180deg,#0C69C8 0%,#0A1D44 100%)", border: "1px solid rgba(255,255,255,.16)" }}>
        <div className="tournament-panel-scan" aria-hidden="true" />
        <div className="tournament-real-topbar">
          <div className="tournament-real-brand">
            <span>SP</span>
            <strong>ProAm Tournaments</strong>
          </div>
          <div className="tournament-real-admin-pill">Admin</div>
        </div>
        <div className="tournament-real-stage">
          <div className="tournament-real-title-block">
            <div className="tournament-real-kicker">Live tournament</div>
            <div className="tournament-real-title">Saturday Open</div>
            <div className="tournament-real-meta"><span /> Live · 8 players · 10:30</div>
            <div className="tournament-real-chip-row">
              <span>Type Padel singles</span>
              <span>Format Single elimination</span>
            </div>
          </div>
          <div className="tournament-real-bracket-label"><span /> Bracket</div>
          <div className="tournament-real-tree-shell">
            <div className="tournament-real-tree-scroll">
              <div className="tournament-real-tree-canvas">
                <svg viewBox="0 0 560 420" aria-hidden="true">
                  {connectorPaths.map((path, index) => (
                    <path key={path} className={`tournament-real-path tournament-real-path-${index + 1}`} d={path} />
                  ))}
                </svg>
                {treeRounds.map((round, roundIndex) => (
                  <React.Fragment key={round.label}>
                    <div className="tournament-real-round-label" style={{ left: round.x, top: 0 }}>{round.label}</div>
                    {round.items.map((player, slotIndex) => {
                      const advanced = player && winnerIds.has(player.id);
                      return (
                        <div
                          key={`${round.label}-${slotIndex}-${player?.id || "empty"}`}
                          className={`tournament-real-node ${roundIndex === 0 ? "is-start" : ""} ${advanced ? "is-advanced" : ""} ${roundIndex === 3 ? "is-winner" : ""}`}
                          style={{ left: round.x, top: round.y[slotIndex], animationDelay: `${(roundIndex * 0.7) + (slotIndex * 0.04)}s` }}
                        >
                          <div className="tournament-real-avatar">
                            <strong>{player ? player.name.charAt(0) : "?"}</strong>
                          </div>
	                          {roundIndex > 0 && <div className="tournament-real-name">{player ? player.name : "TBD"}</div>}
	                          {roundIndex > 0 && player && <div className="tournament-real-user">@seed{player.seed}</div>}
                        </div>
                      );
                    })}
                  </React.Fragment>
                ))}
              </div>
            </div>
          </div>
        </div>
        <div className="tournament-real-admin">
          <div className="tournament-real-admin-head">
            <span className="tournament-real-back" aria-hidden="true">&lt;</span>
            <div>
              <strong>Saturday Open</strong>
              <span>Single elimination</span>
            </div>
            <em>Refresh</em>
          </div>
          <div className="tournament-real-admin-tabs" aria-hidden="true">
            {["Overview", "Format", "Match", "Queue", "Sim", "Admins"].map((tab) => (
              <span key={tab} className={tab === "Match" ? "active" : ""}>{tab}</span>
            ))}
          </div>
          <div className="tournament-real-admin-body">
            <div className="tournament-real-admin-hero">
              <span><FontAwesomeIcon icon={faTrophy} /></span>
              <div>
                <small>Match</small>
                <strong>Score and review games</strong>
                <p>Save a result and the bracket advances live.</p>
              </div>
            </div>
            <div className="tournament-real-score-card">
              <div className="tournament-real-score-head">
                <strong>Next game</strong>
                <span>Final · {resultMap.get("2-0")?.score}</span>
              </div>
              <div className="tournament-real-score-body">
                <div className="tournament-real-score-player">
                  <div className="tournament-real-admin-avatar">N</div>
                  <strong>Noa</strong>
                  <label>Score</label>
                  <div className="tournament-real-score-input">11</div>
                </div>
                <div className="tournament-real-vs">VS</div>
                <div className="tournament-real-score-player">
                  <div className="tournament-real-admin-avatar">I</div>
                  <strong>Ira</strong>
                  <label>Score</label>
                  <div className="tournament-real-score-input">13</div>
                </div>
              </div>
              <div className="tournament-real-save">Save Result</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function WellnessHero({ C, page }) {
  const audiences = [
    ["Friend groups", "Share a code"],
    ["Clubs & communities", "Build teams"],
    ["Companies", "Track departments"],
  ];
  return (
    <section className="wellness-cinema-hero" style={{ position: "relative", overflow: "hidden", padding: "clamp(48px,7vw,92px) 18px clamp(30px,5vw,58px)", background: C.mode === "dark" ? "linear-gradient(180deg,#08111E 0%,#090910 58%,#080810 100%)" : "linear-gradient(180deg,#F6FBFF 0%,#EDF5FF 58%,#F0F2F8 100%)" }}>
      <div className="wellness-hero-grid fu" style={{ position: "relative", zIndex: 1, maxWidth: 1220, margin: "0 auto", display: "grid", gridTemplateColumns: "minmax(0,0.88fr) minmax(390px,1.12fr)", gap: "clamp(26px,5vw,64px)", alignItems: "center" }}>
        <div className="wellness-hero-copy">
          <h1 className="wellness-hero-title" style={{ color: C.text, fontFamily: "'Sora',sans-serif", fontSize: "clamp(34px,4.35vw,56px)", fontWeight: 950, lineHeight: 1.01, letterSpacing: 0, margin: "0 0 18px", maxWidth: 680 }}>
            <span style={{ display: "block", whiteSpace: "nowrap" }}>Move together.</span>
            <span className="wellness-gradient-word" style={{ display: "block", whiteSpace: "nowrap" }}>Compete together.</span>
            <span style={{ display: "block", whiteSpace: "nowrap" }}>Grow together.</span>
          </h1>
          <p style={{ color: C.textSec, fontSize: "clamp(15px,1.7vw,18px)", lineHeight: 1.48, maxWidth: 560, margin: "0 0 20px", fontWeight: 800 }}>
            Start with an invite code, organise people into the right groups, then turn every step into a live leaderboard.
          </p>
          {page.hero.sub && (
            <p style={{ color: C.textSec, fontSize: "clamp(16px,2vw,21px)", lineHeight: 1.48, maxWidth: 680, margin: "0 0 24px", fontWeight: 750 }}>
              {page.hero.sub}
            </p>
          )}
          <div className="wellness-hero-actions" style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "center" }}>
            <CTAButton C={C} to={page.hero.primaryTo} primary>{page.hero.primary}</CTAButton>
          </div>
          <div className="wellness-audience-pills" aria-label="Wellness audiences" style={{ display: "grid", gridTemplateColumns: "repeat(3,minmax(0,1fr))", gap: 8, marginTop: 18, maxWidth: 560 }}>
            {audiences.map(([audience, action], index) => (
              <span key={audience} style={{ display: "grid", gap: 4, minHeight: 58, padding: "10px 11px", borderRadius: 15, color: C.textSec, background: C.mode === "dark" ? "rgba(255,255,255,.045)" : "rgba(255,255,255,.72)", border: `1px solid ${index === 0 ? C.accentBorder : C.border}`, fontSize: 12, fontWeight: 900 }}>
                <span aria-hidden="true" style={{ width: 7, height: 7, borderRadius: 7, background: C.accent }} />
                <span style={{ color: C.text, fontWeight: 950 }}>{audience}</span>
                <span style={{ color: C.textSec, fontSize: 11, fontWeight: 850 }}>{action}</span>
              </span>
            ))}
          </div>
        </div>
        <WellnessHeroVisual C={C} />
      </div>
    </section>
  );
}

function WellnessHeroVisual({ C }) {
  const leaderboard = [
    ["Harbour Social", "1.24M", 88, C.accent],
    ["City College", "980k", 72, "#00A98F"],
    ["North Ops", "760k", 56, "#F59E0B"],
  ];

  return (
    <div className="wellness-hero-visual" aria-label="Animated preview of a Snows ProAm workspace, mobile challenge, and live dashboard" style={{ position: "relative", minHeight: 610 }}>
      <div className="wellness-visual-shell" style={{ position: "absolute", inset: "5% 0 0 auto", width: "80%", minHeight: 470, borderRadius: 28, border: `1px solid ${C.accentBorder}`, background: C.mode === "dark" ? "linear-gradient(145deg,rgba(18,18,31,.94),rgba(8,8,16,.96))" : "linear-gradient(145deg,#FFFFFF,#EEF6FF)", boxShadow: C.shadowLg, padding: "18px 18px 18px clamp(148px,22vw,196px)" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, marginBottom: 14 }}>
          <div>
            <div style={{ color: C.accent, fontSize: 11, fontWeight: 950, textTransform: "uppercase", letterSpacing: .6 }}>Live workspace</div>
            <div style={{ marginTop: 5, color: C.text, fontFamily: "'Sora',sans-serif", fontSize: 22, fontWeight: 950 }}>Harbour Step League</div>
          </div>
          <div className="wellness-live-pill" style={{ color: "#166534", background: "rgba(34,197,94,.12)", border: "1px solid rgba(34,197,94,.28)", borderRadius: 999, padding: "7px 10px", fontSize: 11, fontWeight: 950 }}>Live</div>
        </div>

        <div className="wellness-visual-grid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
          {[
            ["10.27M", "steps tracked"],
            ["795", "members"],
            ["7,827 km", "distance"],
            ["8", "countries"],
          ].map(([value, label], index) => (
            <div key={label} className="wellness-live-stat" style={{ border: `1px solid ${index === 0 ? C.accentBorder : C.border}`, background: index === 0 ? C.accentBg : C.mode === "dark" ? "rgba(255,255,255,.04)" : "#F8FBFF", borderRadius: 16, padding: 13 }}>
              <div style={{ color: index === 0 ? C.accent : C.text, fontFamily: "'Sora',sans-serif", fontSize: 22, fontWeight: 950, lineHeight: 1 }}>{value}</div>
              <div style={{ marginTop: 4, color: C.textSec, fontSize: 11, fontWeight: 900, textTransform: "uppercase", letterSpacing: .45 }}>{label}</div>
            </div>
          ))}
        </div>

        <div style={{ marginTop: 12, display: "grid", gap: 9 }}>
          {leaderboard.map(([team, value, progress, color], index) => (
            <div key={team} className="wellness-rank-row" style={{ display: "grid", gridTemplateColumns: "22px minmax(0,1fr) 54px", gap: 10, alignItems: "center", padding: "10px 11px", borderRadius: 15, background: C.mode === "dark" ? "rgba(255,255,255,.04)" : "#FFFFFF", border: `1px solid ${index === 0 ? C.accentBorder : C.border}` }}>
              <div style={{ color, fontFamily: "'Sora',sans-serif", fontSize: 13, fontWeight: 950 }}>{index + 1}</div>
              <div style={{ minWidth: 0 }}>
                <div style={{ color: C.text, fontSize: 13, fontWeight: 900, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{team}</div>
                <div style={{ marginTop: 7, height: 7, borderRadius: 999, background: C.mode === "dark" ? "rgba(255,255,255,.09)" : "#E5EEF8", overflow: "hidden" }}>
                  <div className="wellness-progress-fill" style={{ width: `${progress}%`, height: "100%", borderRadius: 999, background: `linear-gradient(90deg,${color},${C.accentAlt})` }} />
                </div>
              </div>
              <div style={{ color: C.textSec, fontSize: 12, fontWeight: 950, textAlign: "right" }}>{value}</div>
            </div>
          ))}
        </div>

        <div style={{ marginTop: 13, border: `1px solid ${C.border}`, background: C.mode === "dark" ? "rgba(255,255,255,.035)" : "#F7FAFF", borderRadius: 18, padding: 14 }}>
          <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "center" }}>
            <div>
              <div style={{ color: C.text, fontSize: 14, fontWeight: 950 }}>Share-ready workspace proof</div>
              <div style={{ color: C.textSec, marginTop: 3, fontSize: 12, fontWeight: 800 }}>Live dashboard, team institutes or department institutes, and exports on eligible plans.</div>
            </div>
            <FontAwesomeIcon icon={faArrowRight} aria-hidden="true" style={{ color: C.accent }} />
          </div>
        </div>
      </div>

      <div className="wellness-phone-preview" style={{ position: "absolute", left: 0, top: 0, width: 282, borderRadius: 34, background: C.mode === "dark" ? "#090910" : "#111827", border: "7px solid rgba(255,255,255,.16)", boxShadow: C.mode === "dark" ? "0 28px 70px rgba(0,0,0,.55)" : "0 28px 70px rgba(10,29,68,.22)", padding: "10px 9px 12px", overflow: "hidden" }}>
        <div style={{ width: 72, height: 18, borderRadius: 999, background: "#000", margin: "0 auto 10px" }} />
        <div style={{ borderRadius: 26, overflow: "hidden", background: "#F4F8FF", color: "#0D275B", minHeight: 405, padding: 11 }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, marginBottom: 10 }}>
            <div style={{ width: 32, height: 32, borderRadius: 18, border: "1px solid #D7E7F8", background: "#FFFFFF", display: "grid", placeItems: "center", color: "#0C69C8", fontSize: 15 }}>
              <FontAwesomeIcon icon={faArrowRight} style={{ transform: "rotate(180deg)" }} />
            </div>
            <div style={{ textAlign: "center", minWidth: 0 }}>
              <div style={{ color: "#64748B", fontSize: 8.5, fontWeight: 950, textTransform: "uppercase", letterSpacing: .8 }}>Institution dashboard</div>
              <div style={{ color: "#0D275B", fontFamily: "'Sora',sans-serif", fontSize: 13.5, fontWeight: 950, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>NovaWorks</div>
            </div>
            <div style={{ width: 32, height: 32, borderRadius: 18, border: "1px solid #D7E7F8", background: "#FFFFFF", display: "grid", placeItems: "center", color: "#0C69C8", fontSize: 14 }}>
              <FontAwesomeIcon icon={faUsers} />
            </div>
          </div>

          <div style={{ border: "1px solid #B9D8FF", background: "#FFFFFF", borderRadius: 18, padding: 11, boxShadow: "0 10px 26px rgba(13,39,91,.08)" }}>
            <div style={{ display: "grid", gridTemplateColumns: "42px minmax(0,1fr) auto", gap: 10, alignItems: "center" }}>
              <div style={{ width: 42, height: 42, borderRadius: 14, background: "linear-gradient(135deg,#E6F0FF,#FFFFFF)", border: "1px solid #D7E7F8", display: "grid", placeItems: "center", color: "#0C69C8", fontSize: 18, fontWeight: 950 }}>N</div>
              <div style={{ minWidth: 0 }}>
                <div style={{ color: "#0C69C8", fontSize: 13, fontWeight: 950, fontFamily: "'Sora',sans-serif" }}>Business Scale</div>
                <div style={{ marginTop: 2, color: "#334155", fontSize: 10, fontWeight: 900 }}>Company · 48 members</div>
              </div>
              <div style={{ color: "#D4A106", background: "#FFF7D6", borderRadius: 999, padding: "4px 7px", fontSize: 8.5, fontWeight: 950 }}>Active</div>
            </div>

            <div style={{ marginTop: 10, border: "1px solid #9CCBFF", background: "#EAF4FF", borderRadius: 14, padding: "9px 10px", textAlign: "center", color: "#0C69C8", fontSize: 10.5, fontWeight: 950 }}>
              Copy invite
            </div>
            <div style={{ marginTop: 8, border: "1px solid #F6D46F", background: "#FFF8DF", borderRadius: 14, padding: 10 }}>
              <div style={{ color: "#64748B", fontSize: 8.5, fontWeight: 950, textTransform: "uppercase", letterSpacing: .6 }}>Invite code</div>
              <div style={{ color: "#0F172A", fontFamily: "'Sora',sans-serif", fontSize: 18, fontWeight: 950, letterSpacing: 1.4 }}>NOVA4071</div>
            </div>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(3,minmax(0,1fr))", gap: 7, marginTop: 9 }}>
            {[
              ["22,769", "Steps"],
              ["48/80", "People"],
              ["7", "Teams"],
            ].map(([value, label], index) => (
              <div key={label} style={{ background: "#FFFFFF", border: `1px solid ${index === 0 ? "#B9D8FF" : "#E0E7EF"}`, borderRadius: 14, padding: "8px 7px" }}>
                <div style={{ color: index === 0 ? "#0C69C8" : "#0D275B", fontFamily: "'Sora',sans-serif", fontSize: 12, fontWeight: 950 }}>{value}</div>
                <div style={{ marginTop: 2, color: "#64748B", fontSize: 7.5, fontWeight: 950, textTransform: "uppercase" }}>{label}</div>
              </div>
            ))}
          </div>

          <div style={{ marginTop: 9, background: "#FFFFFF", border: "1px solid #E0E7EF", borderRadius: 17, padding: 10 }}>
            <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "center" }}>
              <div>
                <div style={{ color: "#0F172A", fontSize: 11, fontWeight: 950 }}>Capacity</div>
                <div style={{ color: "#334155", fontSize: 9, fontWeight: 900 }}>48/80 members</div>
              </div>
              <div style={{ color: "#0C69C8", fontFamily: "'Sora',sans-serif", fontSize: 13, fontWeight: 950 }}>60%</div>
            </div>
            <div style={{ marginTop: 7, height: 6, borderRadius: 999, background: "#DCEBFB", overflow: "hidden" }}>
              <div className="wellness-phone-fill" style={{ height: "100%", width: "60%", borderRadius: 999, background: "#0C69C8" }} />
            </div>
          </div>

          <div style={{ marginTop: 9, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
            <div>
              <div style={{ color: "#0F172A", fontSize: 11.5, fontWeight: 950 }}>Top members</div>
              <div style={{ color: "#64748B", fontSize: 8.5, fontWeight: 900 }}>48/80 members</div>
            </div>
            <div style={{ color: "#0C69C8", background: "#EAF4FF", border: "1px solid #B9D8FF", borderRadius: 12, padding: "6px 8px", fontSize: 9, fontWeight: 950 }}>Add</div>
          </div>

          <div style={{ display: "grid", gap: 7, marginTop: 8 }}>
            {[
              ["1", "Ava Kelly", "Member", "13,974", "#FFF8DF", "#F6D46F", "#D4A106"],
              ["2", "Ben Carter", "Employee", "8,795", "#E0F7F4", "#92E2D7", "#14B8A6"],
              ["3", "Mia Nolan", "Member", "6,420", "#EAF4FF", "#B9D8FF", "#0C69C8"],
            ].map(([rank, name, typeLabel, steps, bg, border, accent]) => (
              <div key={name} style={{ display: "grid", gridTemplateColumns: "18px 30px minmax(0,1fr) auto", gap: 7, alignItems: "center", background: bg, border: `1px solid ${border}`, borderRadius: 15, padding: "7px 8px" }}>
                <div style={{ color: accent, fontFamily: "'Sora',sans-serif", fontSize: 11, fontWeight: 950 }}>{rank}</div>
                <div style={{ width: 30, height: 30, borderRadius: 15, background: "#FFFFFF", display: "grid", placeItems: "center", color: accent, fontSize: 11, fontWeight: 950 }}>{name.charAt(0)}</div>
                <div style={{ minWidth: 0 }}>
                  <div style={{ color: "#0F172A", fontSize: 10.5, fontWeight: 950, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{name}</div>
                  <div style={{ color: "#64748B", fontSize: 8.2, fontWeight: 900 }}>{typeLabel}</div>
                </div>
                <div style={{ color: accent, fontFamily: "'Sora',sans-serif", fontSize: 12, fontWeight: 950 }}>{steps}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function EventsHero({ C, page }) {
  return (
    <section className="events-cinema-hero" style={{ position: "relative", overflow: "hidden", padding: "clamp(58px,8vw,112px) 18px 54px", background: C.mode === "dark" ? "linear-gradient(180deg,#07101F 0%,#080810 100%)" : "linear-gradient(180deg,#EAF4FF 0%,#F0F2F8 100%)" }}>
      <div className="tournament-cinema-bg" aria-hidden="true" />
      <div style={{ position: "relative", zIndex: 1, maxWidth: 980, margin: "0 auto", textAlign: "center" }}>
        <div className="fu">
          <h1 className="events-hero-title" style={{ fontFamily: "'Sora',sans-serif", fontSize: "clamp(48px,8vw,104px)", fontWeight: 950, lineHeight: .88, letterSpacing: 0, margin: "0 auto 18px", maxWidth: 900 }}>
            Fill Your Next <span className="events-gradient-word">Event</span>
          </h1>
          <p style={{ color: C.textSec, fontSize: "clamp(16px,2vw,21px)", lineHeight: 1.42, maxWidth: 680, margin: "0 auto 24px", fontWeight: 750 }}>
            {page.hero.sub}
          </p>
          <div className="events-hero-actions" style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "center", justifyContent: "center" }}>
            <CTAButton C={C} to={page.hero.primaryTo} primary>{page.hero.primary}</CTAButton>
            <CTAButton C={C} to={page.hero.secondaryTo}>{page.hero.secondary}</CTAButton>
          </div>
          <p className="events-trust-line" style={{ margin: "14px auto 0", color: C.textSec, fontSize: 13, fontWeight: 850 }}>
            Clear listing. Relevant athletes. Simple registration flow.
          </p>
        </div>
      </div>
    </section>
  );
}

function CoachingHero({ C, page }) {
  return (
    <section className="coaching-cinema-hero" style={{ position: "relative", overflow: "hidden", padding: "clamp(58px,8vw,112px) 18px 54px", background: C.mode === "dark" ? "linear-gradient(180deg,#07101F 0%,#080810 100%)" : "linear-gradient(180deg,#EAF4FF 0%,#F0F2F8 100%)" }}>
      <div className="tournament-cinema-bg" aria-hidden="true" />
      <div style={{ position: "relative", zIndex: 1, maxWidth: 980, margin: "0 auto", textAlign: "center" }}>
        <div className="fu">
          <h1 className="coaching-hero-title" style={{ fontFamily: "'Sora',sans-serif", fontSize: "clamp(48px,8vw,104px)", fontWeight: 950, lineHeight: .88, letterSpacing: 0, margin: "0 auto 18px", maxWidth: 900 }}>
            Coach With <span className="coaching-gradient-word">Clarity</span>
          </h1>
          <p style={{ color: C.textSec, fontSize: "clamp(16px,2vw,21px)", lineHeight: 1.42, maxWidth: 760, margin: "0 auto 24px", fontWeight: 750 }}>
            {page.hero.sub}
          </p>
          <div className="coaching-hero-actions" style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "center", justifyContent: "center" }}>
            <CTAButton C={C} to={page.hero.primaryTo} primary>{page.hero.primary}</CTAButton>
            <CTAButton C={C} to={page.hero.secondaryTo}>{page.hero.secondary}</CTAButton>
          </div>
          <p className="coaching-trust-line" style={{ margin: "14px auto 0", color: C.textSec, fontSize: 13, fontWeight: 850 }}>
            Groups, targets, workouts, nutrition, and attention status in one coaching view.
          </p>
        </div>
      </div>
    </section>
  );
}

function WellnessValueStrip({ C }) {
  const stories = [
    {
      icon: faUsers,
      audience: "Friend groups",
      title: "Turn the group chat into a leaderboard.",
      body: "Five friends should not need admin software. Create the workspace, share the code, and let everyone follow the step race from the app.",
      plan: "Friends, Crew, Club",
      stats: ["5 to 30 people", "Step leaderboard", "App dashboard"],
      color: C.accent,
    },
    {
      icon: faTrophy,
      audience: "Clubs and communities",
      title: "Give members a reason to keep showing up.",
      body: "Sports clubs, schools, charity groups, and social communities can organise members into teams, run activities, and make progress visible.",
      plan: "Club Starter to Club Elite",
      stats: ["50 to 300+ members", "Custom branding", "Team institutes"],
      color: C.accent,
    },
    {
      icon: faBuilding,
      audience: "Companies",
      title: "Run workplace wellbeing with structure.",
      body: "Companies can organise departments, launch challenges, track participation, and use full web dashboards, analytics, and exports on eligible plans.",
      plan: "Enterprise rollouts",
      stats: ["Full web dashboard", "Member and department institutes", "Analytics and export"],
      color: C.accent,
    },
  ];

  return (
    <section id="wellness-stories" className="fu" aria-labelledby="wellness-stories-title" style={{ maxWidth: 1220, margin: "0 auto", padding: "18px 18px 34px" }}>
      <div className="wellness-story-grid biz-grid" style={{ display: "grid", gridTemplateColumns: "repeat(3,minmax(0,1fr))", gap: 14 }}>
        {stories.map((story, index) => (
          <article key={story.audience} className="wellness-story-card proam-motion-card" style={{ position: "relative", overflow: "hidden", minHeight: 214, borderRadius: 22, padding: 20, border: `1px solid ${C.border}`, background: C.bgCard, boxShadow: "none", display: "flex", flexDirection: "column" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, marginBottom: 18 }}>
              <div style={{ width: 46, height: 46, borderRadius: 16, display: "grid", placeItems: "center", background: `linear-gradient(135deg,${C.accent},${C.accentAlt})`, color: "#fff", boxShadow: `0 12px 30px ${C.accentGlow}` }}>
                <FontAwesomeIcon icon={story.icon} aria-hidden="true" />
              </div>
              <div style={{ color: C.accent, background: C.mode === "dark" ? "rgba(255,255,255,.045)" : "rgba(255,255,255,.72)", border: `1px solid ${C.accentBorder}`, borderRadius: 999, padding: "7px 9px", fontSize: 11, fontWeight: 950, textTransform: "uppercase", letterSpacing: .45 }}>
                {story.audience}
              </div>
            </div>
            <h3 style={{ margin: 0, color: C.text, fontFamily: "'Sora',sans-serif", fontSize: "clamp(22px,2.8vw,29px)", lineHeight: 1.02 }}>{story.title}</h3>
            <div style={{ marginTop: "auto", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, borderTop: `1px solid ${C.border}`, paddingTop: 14 }}>
              <div>
                <div style={{ color: C.textMuted, fontSize: 10.5, fontWeight: 950, textTransform: "uppercase", letterSpacing: .5 }}>Plan lane</div>
                <div style={{ color: C.accent, marginTop: 3, fontSize: 12.5, fontWeight: 950 }}>{story.plan}</div>
              </div>
            </div>
          </article>
        ))}
      </div>
    </section>
  );
}

function TournamentServiceGrid({ C }) {
  const steps = [
    { icon: faTrophy, title: "Create", body: "Set sport, format, entry price, start time, capacity, and the player details people need before joining." },
    { icon: faUsers, title: "Fill", body: "Share one event link, keep open spots visible, and reduce the group-chat chase before brackets are built." },
    { icon: faLayerGroup, title: "Run", body: "Move from signups to bracket, queue, scores, and live admin controls when the event is underway." },
  ];
  return (
    <section className="fu" aria-labelledby="tournament-flow-title" style={{ maxWidth: 1220, margin: "0 auto", padding: "34px 18px 16px" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", gap: 18, marginBottom: 16, flexWrap: "wrap" }}>
        <div>
          <div style={{ color: C.accent, fontSize: 11, fontWeight: 950, textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 7 }}>Built for event day</div>
          <h2 id="tournament-flow-title" className="tournament-flow-title" style={{ margin: 0, color: C.text, fontFamily: "'Sora',sans-serif", fontSize: "clamp(30px,4.8vw,52px)", fontWeight: 850, lineHeight: 1.02, letterSpacing: 0, maxWidth: 920 }}>
            Create the event. Fill the bracket. Run the day.
          </h2>
        </div>
        <a href="#pricing" className="proam-action" style={{ textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 8, border: `1px solid ${C.accentBorder}`, background: C.accentBg, color: C.accent, borderRadius: 13, padding: "11px 14px", fontFamily: "'Sora',sans-serif", fontWeight: 900 }}>
          Compare access <FontAwesomeIcon icon={faArrowRight} />
        </a>
      </div>
      <div className="biz-grid" style={{ display: "grid", gridTemplateColumns: "repeat(3,minmax(0,1fr))", gap: 12 }}>
        {steps.map((step, index) => (
          <article key={step.title} className="proam-motion-card tournament-flow-card" style={{ position: "relative", overflow: "hidden", background: index === 1 ? C.accentGrad : C.bgCard, border: `1px solid ${index === 1 ? "transparent" : C.border}`, borderRadius: 22, padding: 20, minHeight: 220, color: index === 1 ? "#fff" : C.text, boxShadow: index === 1 ? C.shadowLg : "none" }}>
            <div aria-hidden="true" style={{ position: "absolute", right: -24, top: -24, width: 96, height: 96, borderRadius: 96, background: index === 1 ? "rgba(255,255,255,.18)" : C.accentBg }} />
            <div style={{ position: "relative", width: 42, height: 42, borderRadius: 14, display: "grid", placeItems: "center", background: index === 1 ? "rgba(255,255,255,.16)" : C.accentBg, border: `1px solid ${index === 1 ? "rgba(255,255,255,.22)" : C.accentBorder}`, color: index === 1 ? "#fff" : C.accent, marginBottom: 16 }}>
              <FontAwesomeIcon icon={step.icon} />
            </div>
            <div style={{ position: "relative", color: index === 1 ? "rgba(255,255,255,.72)" : C.accent, fontFamily: "'Sora',sans-serif", fontSize: 26, fontWeight: 950, marginBottom: 12 }}>{String(index + 1).padStart(2, "0")}</div>
            <h3 style={{ position: "relative", margin: "0 0 9px", color: index === 1 ? "#fff" : C.text, fontFamily: "'Sora',sans-serif", fontSize: 26, lineHeight: 1 }}>{step.title}</h3>
            <p style={{ position: "relative", margin: 0, color: index === 1 ? "rgba(255,255,255,.82)" : C.textSec, fontSize: 14, fontWeight: 800, lineHeight: 1.45 }}>{step.body}</p>
          </article>
        ))}
      </div>
    </section>
  );
}

function TournamentValueStrip({ C }) {
  const paths = [
    { icon: faUsers, title: "Players", body: "Find the event details quickly, see the spot, join from the app, and know where to go next.", label: "Discover and join", color: C.accent },
    { icon: faBuilding, title: "Clubs", body: "Publish one clear tournament page, share it with members, and keep capacity visible as entries come in.", label: "Fill the bracket", color: "#00A98F" },
    { icon: faClipboardCheck, title: "Organizers", body: "Use paid access when formats, web admin controls, and live tournament operations matter on the day.", label: "Run event day", color: "#F59E0B" },
  ];
  return (
    <section className="fu" aria-labelledby="tournament-path-title" style={{ maxWidth: 1220, margin: "0 auto", padding: "8px 18px 28px" }}>
      <div style={{ display: "grid", gridTemplateColumns: "minmax(0,.72fr) minmax(320px,1.28fr)", gap: 16, alignItems: "stretch" }} className="biz-grid">
        <div style={{ border: `1px solid ${C.accentBorder}`, background: C.mode === "dark" ? "linear-gradient(135deg,#101423,#080810)" : "linear-gradient(135deg,#FFFFFF,#EEF7FF)", borderRadius: 24, padding: "clamp(20px,3vw,28px)", boxShadow: C.shadowLg }}>
          <div style={{ color: C.accent, fontSize: 11, fontWeight: 950, textTransform: "uppercase", letterSpacing: .6, marginBottom: 8 }}>Who it helps</div>
          <h2 id="tournament-path-title" style={{ margin: 0, color: C.text, fontFamily: "'Sora',sans-serif", fontSize: "clamp(28px,4.4vw,48px)", lineHeight: 1.02 }}>
            One page for every role around the event.
          </h2>
          <p style={{ margin: "12px 0 0", color: C.textSec, fontSize: 15, fontWeight: 750, lineHeight: 1.58 }}>
            Players need clarity, clubs need signups, and organizers need control. The tournament page should make each next action obvious.
          </p>
        </div>
        <div className="biz-grid" style={{ display: "grid", gridTemplateColumns: "repeat(3,minmax(0,1fr))", gap: 12 }}>
          {paths.map((path, index) => (
            <article key={path.title} className="proam-motion-card tournament-path-card" style={{ position: "relative", overflow: "hidden", border: `1px solid ${index === 2 ? "rgba(245,158,11,.30)" : C.border}`, background: index === 1 ? `linear-gradient(145deg,rgba(0,169,143,.10),${C.bgCard})` : index === 2 ? `linear-gradient(145deg,rgba(245,158,11,.12),${C.bgCard})` : C.bgCard, borderRadius: 22, padding: 18, minHeight: 260 }}>
              <div style={{ width: 44, height: 44, borderRadius: 15, display: "grid", placeItems: "center", background: `linear-gradient(135deg,${path.color},${index === 2 ? "#EF4E7B" : C.accentAlt})`, color: "#fff", marginBottom: 15 }}>
                <FontAwesomeIcon icon={path.icon} aria-hidden="true" />
              </div>
              <div style={{ color: path.color, fontSize: 11, fontWeight: 950, textTransform: "uppercase", letterSpacing: .5, marginBottom: 7 }}>{path.label}</div>
              <h3 style={{ margin: 0, color: C.text, fontFamily: "'Sora',sans-serif", fontSize: 23, lineHeight: 1.06 }}>{path.title}</h3>
              <p style={{ margin: "10px 0 0", color: C.textSec, fontSize: 13.5, fontWeight: 750, lineHeight: 1.52 }}>{path.body}</p>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

function CoachingValueStrip({ C }) {
  const values = [
    ["Assign", "Give each group the sessions, workouts, and targets they need."],
    ["Track", "See completion, feedback, calories, and recent activity clearly."],
    ["Act", "Spot who is ahead, on plan, building momentum, or needs attention."],
  ];
  return (
    <section className="fu" style={{ maxWidth: 1180, margin: "0 auto", padding: "10px 18px 28px" }}>
      <div className="biz-grid" style={{ display: "grid", gridTemplateColumns: "repeat(3,minmax(0,1fr))", gap: 12 }}>
        {values.map(([title, body], index) => (
          <article key={title} className="proam-motion-card" style={{ minHeight: 180, borderRadius: 20, padding: 20, border: `1px solid ${index === 2 ? C.accentBorder : C.border}`, background: index === 2 ? `linear-gradient(145deg,${C.accentBg},${C.bgCard})` : C.bgCard, boxShadow: index === 2 ? C.shadowLg : "none" }}>
            <div style={{ color: C.accent, fontFamily: "'Sora',sans-serif", fontSize: 32, fontWeight: 950, marginBottom: 18 }}>{String(index + 1).padStart(2, "0")}</div>
            <h2 style={{ margin: "0 0 8px", color: C.text, fontFamily: "'Sora',sans-serif", fontSize: 24, lineHeight: 1.05 }}>{title}</h2>
            <p style={{ margin: 0, color: C.textSec, fontSize: 14, fontWeight: 750, lineHeight: 1.45 }}>{body}</p>
          </article>
        ))}
      </div>
    </section>
  );
}

function EventsCompactExplainer({ C }) {
  const feeRows = [
    ["Listing", "Required before review", "#2B7FFF"],
    ["Reach", "Optional radius boost", "#00C2A8"],
    ["Payment", "Card or ProAm Coins", "#F59E0B"],
    ["Review", "Usually shown as 24 hours", "#EF4E7B"],
  ];
  return (
    <section id="event-flow" className="fu" style={{ maxWidth: 1100, margin: "0 auto", padding: "26px 18px 10px" }}>
      <div
        className="biz-grid"
        style={{
          display: "grid",
          gridTemplateColumns: "minmax(0,0.95fr) minmax(300px,1.05fr)",
          gap: 16,
          alignItems: "stretch",
        }}
      >
        <div style={{ position: "relative", overflow: "hidden", border: "1px solid rgba(255,255,255,.20)", background: "linear-gradient(135deg,#143BFF 0%,#00A6FF 42%,#00C2A8 100%)", borderRadius: 24, padding: "clamp(20px,3vw,30px)", boxShadow: "0 22px 70px rgba(0,126,255,.28)", color: "#fff", display: "flex", flexDirection: "column", justifyContent: "space-between", minHeight: 330 }}>
          <div aria-hidden="true" style={{ position: "absolute", inset: "auto -18% -35% 18%", height: 220, background: "linear-gradient(135deg,rgba(255,255,255,.24),rgba(255,255,255,0))", transform: "rotate(-8deg)" }} />
          <div aria-hidden="true" style={{ position: "absolute", inset: "-28% 18% auto -18%", height: 210, background: "linear-gradient(135deg,rgba(245,158,11,.46),rgba(239,78,123,.08))", transform: "rotate(10deg)" }} />
          <div>
            <div style={{ position: "relative", display: "inline-flex", alignItems: "center", border: "1px solid rgba(255,255,255,.28)", background: "rgba(255,255,255,.16)", borderRadius: 999, padding: "7px 11px", fontSize: 11, fontWeight: 950, textTransform: "uppercase", letterSpacing: .6 }}>
              Create in the app
            </div>
            <h2 style={{ position: "relative", margin: "16px 0 10px", fontFamily: "'Sora',sans-serif", fontSize: "clamp(34px,5vw,58px)", fontWeight: 950, lineHeight: .9 }}>
              Set it up once. Let the app price it.
            </h2>
            <p style={{ position: "relative", margin: 0, color: "rgba(255,255,255,.84)", fontSize: 15, fontWeight: 800, lineHeight: 1.5, maxWidth: 460 }}>
              Add the event details in Snows ProAm. Before you submit, the app shows the listing fee, optional reach fee, and final total.
            </p>
          </div>
          <div style={{ position: "relative", marginTop: 24, display: "grid", gap: 10 }}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
              {["Title + sport", "Date + place", "Capacity", "Entry fee"].map((label) => (
                <div key={label} style={{ border: "1px solid rgba(255,255,255,.20)", background: "rgba(7,16,31,.22)", borderRadius: 14, padding: "11px 12px", fontSize: 12, fontWeight: 950 }}>
                  {label}
                </div>
              ))}
            </div>
            <CTAButton C={{ ...C, accentGrad: "linear-gradient(135deg,#07101F,#0A1D44)", accentGlow: "rgba(0,0,0,.28)" }} to="https://linktr.ee/snowsproam" primary>Open Snows ProAm App</CTAButton>
          </div>
        </div>

        <div style={{ border: `1px solid ${C.border}`, background: C.mode === "dark" ? "linear-gradient(145deg,#12121F,#0B1020)" : "linear-gradient(145deg,#FFFFFF,#F4F8FF)", borderRadius: 24, padding: "clamp(18px,3vw,24px)", boxShadow: C.shadowLg }}>
          <div style={{ border: `1px solid ${C.borderMd}`, background: C.bgElevated, borderRadius: 20, padding: 16 }}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr auto", alignItems: "start", gap: 12, marginBottom: 14 }}>
              <div>
                <div style={{ color: C.textSec, fontSize: 11, fontWeight: 950, letterSpacing: .5, textTransform: "uppercase" }}>Fee preview</div>
                <h3 style={{ margin: "4px 0 0", color: C.text, fontFamily: "'Sora',sans-serif", fontSize: 24, fontWeight: 950, lineHeight: 1.05 }}>In-app total</h3>
                <p style={{ margin: "5px 0 0", color: C.textSec, fontSize: 12.5, fontWeight: 800, lineHeight: 1.35 }}>Snows ProAm calculates fees before you submit.</p>
              </div>
              <div style={{ borderRadius: 12, padding: "7px 9px", background: C.accentGrad, color: "#fff", fontFamily: "'Sora',sans-serif", fontSize: 11, fontWeight: 950, whiteSpace: "nowrap" }}>Before submit</div>
            </div>

            <div style={{ display: "grid", gap: 9 }}>
              {feeRows.map(([title, body, color]) => (
                <div key={title} style={{ display: "grid", gridTemplateColumns: "42px 1fr auto", alignItems: "center", gap: 12, border: `1px solid ${C.border}`, background: C.mode === "dark" ? "rgba(255,255,255,.04)" : "#FFFFFF", borderRadius: 15, padding: 12 }}>
                  <div aria-hidden="true" style={{ width: 42, height: 42, borderRadius: 14, background: `${color}22`, color, display: "grid", placeItems: "center", fontSize: 15 }}>
                    <FontAwesomeIcon icon={title === "Review" ? faCheck : faArrowRight} />
                  </div>
                  <div>
                    <div style={{ color: C.text, fontFamily: "'Sora',sans-serif", fontSize: 14, fontWeight: 950 }}>{title}</div>
                    <div style={{ marginTop: 2, color: C.textSec, fontSize: 12.5, fontWeight: 800 }}>{body}</div>
                  </div>
                  <div aria-hidden="true" style={{ width: 8, height: 38, borderRadius: 999, background: color }} />
                </div>
              ))}
            </div>
          </div>

          <div style={{ marginTop: 12, display: "grid", gridTemplateColumns: "repeat(2,minmax(0,1fr))", gap: 10 }}>
            <div style={{ border: "1px solid rgba(0,194,168,.28)", borderRadius: 16, padding: 14, background: "linear-gradient(135deg,rgba(0,194,168,.16),rgba(43,127,255,.08))" }}>
              <div style={{ color: "#00A98F", fontFamily: "'Sora',sans-serif", fontSize: 22, fontWeight: 950 }}>2</div>
              <div style={{ color: C.textSec, fontSize: 12, fontWeight: 850, marginTop: 3 }}>Boost reminders after approval</div>
            </div>
            <div style={{ border: "1px solid rgba(245,158,11,.30)", borderRadius: 16, padding: 14, background: "linear-gradient(135deg,rgba(245,158,11,.18),rgba(239,78,123,.08))" }}>
              <div style={{ color: "#F59E0B", fontFamily: "'Sora',sans-serif", fontSize: 22, fontWeight: 950 }}>Live</div>
              <div style={{ color: C.textSec, fontSize: 12, fontWeight: 850, marginTop: 3 }}>Approved events appear after review</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Pricing({ C, plans, target = "#trial", type, institutionBusinessDemoMode = false, institutionBusinessDemoCalendlyUrl = "" }) {
  const [checkoutLoading, setCheckoutLoading] = useState(null);
  const [checkoutMessage, setCheckoutMessage] = useState("");
  const [checkoutModalOpen, setCheckoutModalOpen] = useState(false);
  const [coachingBillingCycle, setCoachingBillingCycle] = useState("yearly");
  const [wellnessSegment, setWellnessSegment] = useState(type === "wellness" ? "business" : "all");
  const wellnessMemberCount = 300;
  const [institutionBillingRows, setInstitutionBillingRows] = useState([]);
  const signInRequiredMessage = "You must sign in on the website with the same ProAm account you use in the app before subscribing.";

  const showCheckoutMessage = (message) => {
    setCheckoutMessage(message);
    setCheckoutModalOpen(true);
  };

  useEffect(() => {
    if (type !== "wellness") return undefined;
    let mounted = true;
    loadInstitutionBillingRows()
      .then((rows) => {
        if (mounted) setInstitutionBillingRows(rows);
      })
      .catch(() => {
        if (mounted) setInstitutionBillingRows([]);
      });
    return () => {
      mounted = false;
    };
  }, [type]);

  const getFunctionErrorMessage = async (err) => {
    const fallback = err?.message || "Could not start checkout.";
    const response = err?.context;
    if (!response || typeof response.json !== "function") return fallback;
    try {
      const payload = await response.json();
      return payload?.error || payload?.message || fallback;
    } catch {
      return fallback;
    }
  };

  const startWorkoutCheckout = async (plan) => {
    if (plan.comingSoon) return;
    const priceKey = coachingBillingCycle === "yearly" ? plan.yearlyPriceKey : plan.monthlyPriceKey;

    const { data: sessionData } = await supabase.auth.getSession();
    const session = sessionData?.session;
    if (!session) {
      showCheckoutMessage(signInRequiredMessage);
      return;
    }

    if (!priceKey) {
      showCheckoutMessage("You are signed in. Open your profile to manage Workout Groups access.");
      return;
    }

    setCheckoutLoading(priceKey);
    setCheckoutMessage("");
    try {
      const { data, error } = await supabase.functions.invoke("workout_groups_create_checkout", {
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
        body: withRequestedStripeMode({
          price_key: priceKey,
          success_url: `${window.location.origin}/profile?checkout=workout-groups-success`,
          cancel_url: `${window.location.origin}/coaching?checkout=cancelled`,
        }),
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      if (data?.url) window.location.href = data.url;
    } catch (err) {
      const message = await getFunctionErrorMessage(err);
      showCheckoutMessage(message.toLowerCase().includes("unauthorized") ? signInRequiredMessage : message);
    } finally {
      setCheckoutLoading(null);
    }
  };

  const startTournamentCheckout = async (plan) => {
    if (DISABLE_WEB_TOURNAMENT_CHECKOUT || !plan.priceKey) {
      window.location.href = SNOWS_PROAM_LINKTREE_URL;
      return;
    }

    const { data: sessionData } = await supabase.auth.getSession();
    const session = sessionData?.session;
    if (!session) {
      showCheckoutMessage(signInRequiredMessage);
      return;
    }

    setCheckoutLoading(plan.priceKey);
    setCheckoutMessage("");
    try {
      const { data, error } = await supabase.functions.invoke("tournaments_create_checkout", {
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
        body: withRequestedStripeMode({
          price_key: plan.priceKey,
          success_url: `${window.location.origin}/profile?checkout=tournaments-success`,
          cancel_url: `${window.location.origin}/tournaments?checkout=cancelled`,
        }),
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      if (data?.url) window.location.href = data.url;
    } catch (err) {
      const message = await getFunctionErrorMessage(err);
      showCheckoutMessage(message.toLowerCase().includes("unauthorized") ? signInRequiredMessage : message);
    } finally {
      setCheckoutLoading(null);
    }
  };

  const billingPlans = useMemo(
    () => type === "wellness" ? mergeInstitutionPlansWithBillingRows(plans, institutionBillingRows) : plans,
    [institutionBillingRows, plans, type],
  );

  const visiblePlans = type === "wellness" && wellnessSegment !== "all"
    ? billingPlans.filter((plan) => String(plan.segment || "").toLowerCase() === wellnessSegment)
    : billingPlans;
  const displayPlans = type === "wellness"
    ? collapseBusinessDemoPlans(visiblePlans, institutionBusinessDemoMode)
    : visiblePlans;
  const hasSingleBusinessDemoPlan = type === "wellness"
    && institutionBusinessDemoMode
    && displayPlans.length === 1
    && (displayPlans[0]?.isBusinessDemoCard || isBusinessInstitutionPlan(displayPlans[0]));
  const estimateMemberCount = Math.max(0, Math.floor(Number(wellnessMemberCount) || 0));
  const pricingTitle = type === "wellness"
    ? "Pick the workspace lane."
    : type === "tournaments"
      ? "Choose the tournament access you need."
      : "Pricing";
  const pricingIntro = type === "wellness"
    ? "Start with five friends, step up for a club or community, or choose the full company dashboard when departments, reporting, and exports matter."
    : type === "tournaments"
      ? "Start with one simple bracket for free. Unlock Standard for more formats, or Pro when the live web dashboard and admin controls matter on event day."
      : null;
  const wellnessSegmentOptions = [
    ["business", "Companies"],
    ["social", "Friends"],
    ["club", "Clubs"],
    ["all", "All"],
  ];
  const selectedWellnessSegmentLabel = wellnessSegmentOptions.find(([value]) => value === wellnessSegment)?.[1] || "All";

  return (
    <section id="pricing" className="fu proam-modern-surface" style={{ maxWidth: type === "coaching" ? 1480 : 1180, margin: "0 auto", padding: "42px 18px" }}>
      <div style={{ textAlign: "center", maxWidth: 760, margin: "0 auto 24px" }}>
        <h2 style={{ fontFamily: "'Sora',sans-serif", fontSize: "clamp(30px,5vw,52px)", margin: "0 0 10px" }}>{pricingTitle}</h2>
        {pricingIntro && (
          <p style={{ margin: 0, color: C.textSec, fontSize: 15, fontWeight: 750, lineHeight: 1.55 }}>{pricingIntro}</p>
        )}
      </div>
      {["coaching", "tournaments"].includes(type) && checkoutModalOpen && (
        <div role="status" aria-live="polite" style={{ maxWidth: 720, margin: "0 auto 18px", background: C.bgCard, border: `1px solid ${C.accentBorder}`, borderRadius: 16, padding: "14px 16px", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 14, boxShadow: C.shadowLg }}>
          <p style={{ margin: 0, color: C.textSec, fontSize: 13.5, lineHeight: 1.45, fontWeight: 750 }}>{checkoutMessage}</p>
          <button
            type="button"
            onClick={() => setCheckoutModalOpen(false)}
            style={{ flexShrink: 0, border: `1px solid ${C.borderMd}`, background: C.bgElevated, color: C.text, borderRadius: 10, padding: "8px 10px", fontFamily: "'Sora',sans-serif", fontSize: 12, fontWeight: 900, cursor: "pointer" }}
          >
            Dismiss
          </button>
        </div>
      )}
      {type === "coaching" && (
        <div style={{ display: "flex", justifyContent: "center", marginBottom: 14 }}>
          <div style={{ display: "inline-flex", background: C.bgCard, border: `1px solid ${C.border}`, borderRadius: 14, padding: 4 }}>
            {[
              ["monthly", "Monthly"],
              ["yearly", "Yearly · save 15%"],
            ].map(([value, label]) => (
              <button key={value} type="button" className="proam-action" aria-pressed={coachingBillingCycle === value} onClick={() => setCoachingBillingCycle(value)} style={{ border: "none", borderRadius: 10, padding: "9px 12px", background: coachingBillingCycle === value ? C.accentGrad : "transparent", color: coachingBillingCycle === value ? "#fff" : C.textSec, fontWeight: 900, cursor: "pointer" }}>
                <span>{label}</span>
              </button>
            ))}
          </div>
        </div>
      )}
      {type === "wellness" && (
        <div style={{ display: "grid", justifyItems: "center", gap: 12, marginBottom: 16 }}>
          <div style={{ display: "flex", justifyContent: "center" }}>
            <div style={{ display: "inline-flex", flexWrap: "wrap", justifyContent: "center", gap: 4, background: C.bgCard, border: `1px solid ${C.border}`, borderRadius: 14, padding: 4 }}>
              {wellnessSegmentOptions.map(([value, label]) => (
                <button key={value} type="button" className="proam-action" aria-pressed={wellnessSegment === value} onClick={() => setWellnessSegment(value)} style={{ border: "none", borderRadius: 10, padding: "9px 12px", background: wellnessSegment === value ? C.accentGrad : "transparent", color: wellnessSegment === value ? "#fff" : C.textSec, fontWeight: 900, cursor: "pointer" }}>
                  <span>{label}</span>
                </button>
              ))}
            </div>
          </div>
          <p style={{ margin: 0, maxWidth: 620, color: C.textMuted, fontSize: 12.5, fontWeight: 750, lineHeight: 1.45, textAlign: "center" }}>
            Showing {selectedWellnessSegmentLabel.toLowerCase()} plans. Use All to compare every workspace size.
          </p>
        </div>
      )}
      <div className="price-grid proam-stagger" style={{ display: "grid", gridTemplateColumns: hasSingleBusinessDemoPlan ? "minmax(280px,860px)" : displayPlans.length === 1 ? "minmax(230px,420px)" : type === "coaching" ? "repeat(auto-fit,minmax(220px,1fr))" : "repeat(auto-fit,minmax(230px,1fr))", justifyContent: displayPlans.length === 1 ? "center" : "stretch", gap: type === "coaching" ? 10 : 14 }}>
        {displayPlans.map((plan) => {
          const businessDemoPlan = type === "wellness" && institutionBusinessDemoMode && (plan.isBusinessDemoCard || isBusinessInstitutionPlan(plan));
          const leadBusinessDemoPlan = businessDemoPlan && hasSingleBusinessDemoPlan;
          const planTone = type === "wellness" ? C.accent : plan.segment === "Business" ? "#16A34A" : C.accent;
          const alternatePrice = type === "coaching"
            ? coachingBillingCycle === "yearly" ? plan.monthlyPrice : plan.yearlyPrice
            : null;
          const alternateLabel = coachingBillingCycle === "yearly" ? "Monthly option" : "Yearly option";
          const priceKey = type === "coaching"
            ? coachingBillingCycle === "yearly" ? plan.yearlyPriceKey : plan.monthlyPriceKey
            : plan.priceKey;
          const loadingKey = priceKey || `${type}-${plan.name}`;
          const isPlanLoading = checkoutLoading === loadingKey;
          const disabledTournamentWebCheckout = type === "tournaments" && DISABLE_WEB_TOURNAMENT_CHECKOUT;
          const usageBased = type === "wellness" && !businessDemoPlan && !!plan.metadata?.usage_based;
          const estimateBaseAmountCents = plan.metadata?.estimate_base_amount_cents || plan.baseAmountCents || plan.monthlyAmountCents || 0;
          const estimatedMonthlyCents = usageBased
            ? calculateInstitutionMonthlyPrice(plan.metadata, estimateMemberCount, estimateBaseAmountCents)
            : null;
          const overageBreakdown = usageBased
            ? calculateInstitutionOverageBreakdown(plan.metadata, estimateMemberCount)
            : [];
          const requiresContactAtEstimate = usageBased
            ? institutionPriceRequiresContact(plan.metadata, estimateMemberCount)
            : false;
          const planPrice = businessDemoPlan
            ? plan.price || "Custom pricing"
            : type === "coaching"
            ? coachingBillingCycle === "yearly" ? plan.yearlyPrice : plan.monthlyPrice
            : usageBased
              ? `${formatInstitutionPrice(estimatedMonthlyCents)}/month est.`
              : plan.price;
          return (
          <div key={plan.name} className={`proam-price-card proam-motion-card${leadBusinessDemoPlan ? " business-demo-lead-card" : ""}`} style={{ position: "relative", background: leadBusinessDemoPlan ? (C.mode === "dark" ? "linear-gradient(135deg,rgba(43,127,255,.18),rgba(255,255,255,.045))" : "linear-gradient(135deg,#FFFFFF,#EEF6FF)") : C.bgCard, border: `1.5px solid ${plan.featured ? C.accent : C.border}`, borderRadius: leadBusinessDemoPlan ? 24 : 18, padding: leadBusinessDemoPlan ? "clamp(24px,4vw,36px)" : type === "coaching" ? 14 : 20, boxShadow: plan.featured ? `0 0 0 1px ${C.accentBorder}, 0 18px 60px ${C.accentGlow}` : C.shadowLg }}>
            {plan.badge && <div style={{ display: "inline-flex", background: plan.featured ? C.accentGrad : C.accentBg, color: plan.featured ? "#fff" : C.accent, borderRadius: 999, padding: "5px 9px", fontSize: 10, fontWeight: 900, textTransform: "uppercase", letterSpacing: .5, marginBottom: 14 }}>{plan.badge}</div>}
            <h3 style={{ fontFamily: "'Sora',sans-serif", fontSize: leadBusinessDemoPlan ? "clamp(26px,4vw,42px)" : type === "coaching" ? 16 : 20, textTransform: leadBusinessDemoPlan ? "none" : "uppercase", margin: "0 0 8px", lineHeight: 1.05 }}>{plan.name}</h3>
            <div style={{ fontFamily: "'Sora',sans-serif", fontSize: leadBusinessDemoPlan ? "clamp(22px,3vw,32px)" : type === "coaching" ? 21 : 28, fontWeight: 900, color: plan.featured ? C.accent : C.text, marginBottom: plan.yearlySaving && coachingBillingCycle === "yearly" ? 5 : leadBusinessDemoPlan ? 18 : 16 }}>{planPrice}</div>
            {leadBusinessDemoPlan && (
              <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 12, marginBottom: 22 }}>
                <CTAButton C={C} to={institutionBusinessDemoCalendlyUrl || "#book-demo"} primary>
                  Book a Demo
                </CTAButton>
                <span style={{ color: C.textSec, fontSize: 13, fontWeight: 800, lineHeight: 1.4 }}>For companies that need member and department institutes, dashboards, analytics, and rollout support.</span>
              </div>
            )}
            {plan.description && (
              <div style={{ color: C.textSec, fontSize: 13, fontWeight: 750, lineHeight: 1.45, margin: "-8px 0 14px" }}>{plan.description}</div>
            )}
            {type === "wellness" && plan.perMember && !businessDemoPlan && (
              <div style={{ color: planTone, fontSize: 12, fontWeight: 900, marginTop: -10, marginBottom: 12 }}>{plan.perMember}</div>
            )}
            {usageBased && (
              <div style={{ border: `1px solid ${C.border}`, background: C.mode === "dark" ? "rgba(255,255,255,0.035)" : "#F7FAFF", borderRadius: 12, padding: 10, margin: "-2px 0 14px" }}>
                <div style={{ display: "flex", justifyContent: "space-between", gap: 8, color: C.text, fontSize: 12, fontWeight: 900 }}>
                  <span>{estimateMemberCount.toLocaleString()} active members</span>
                  <span>{formatInstitutionPrice(estimatedMonthlyCents)}/month est.</span>
                </div>
                {requiresContactAtEstimate && (
                  <div style={{ color: planTone, fontSize: 11, fontWeight: 850, marginTop: 6 }}>Request rollout review to confirm pricing.</div>
                )}
                {overageBreakdown.length > 0 && (
                  <div style={{ display: "grid", gap: 4, marginTop: 8 }}>
                    {overageBreakdown.map((line) => (
                      <div key={`${line.from}-${line.to || "plus"}`} style={{ display: "flex", justifyContent: "space-between", gap: 8, color: C.textSec, fontSize: 11, fontWeight: 750 }}>
                        <span>{line.count.toLocaleString()} at {formatInstitutionPrice(line.amount_cents)}/member</span>
                        <span>{formatInstitutionPrice(line.line_total_cents)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
            {type === "coaching" && alternatePrice && alternatePrice !== planPrice && (
              <div style={{ color: alternateLabel === "Monthly option" ? "#FFFFFF" : C.textMuted, fontSize: 12, fontWeight: 850, marginBottom: plan.yearlySaving && coachingBillingCycle === "yearly" ? 4 : 12 }}>{alternateLabel}: {alternatePrice}</div>
            )}
            {type === "coaching" && coachingBillingCycle === "yearly" && plan.yearlySaving && (
              <div style={{ color: "#16A34A", fontSize: 12, fontWeight: 900, marginBottom: 12 }}>{plan.yearlySaving}</div>
            )}
            <div className={leadBusinessDemoPlan ? "business-demo-feature-grid" : undefined} style={{ display: "grid", gridTemplateColumns: leadBusinessDemoPlan ? "repeat(2,minmax(0,1fr))" : "1fr", gap: 9, marginBottom: leadBusinessDemoPlan ? 0 : 20 }}>
              {plan.items.map((item) => {
                const state = planItemState(item);
                const isCapacity = type === "wellness" && /^up to\b/i.test(state.label);
                const visibleLabel = state.unavailable ? item : state.label;
                return (
                  <div key={item} style={{ color: isCapacity ? planTone : C.textSec, fontSize: type === "coaching" ? 12 : 13.5, fontWeight: isCapacity ? 900 : 600, display: "flex", alignItems: "center", gap: 8 }}>
                    <FontAwesomeIcon icon={state.unavailable ? faTimes : state.addon ? faPlus : faCheck} style={{ color: state.unavailable ? "#EF4444" : state.addon ? "#F59E0B" : isCapacity ? planTone : C.accent, fontSize: 12, width: 12, flexShrink: 0 }} />
                    <span>{visibleLabel}</span>
                  </div>
                );
              })}
            </div>
            {plan.note && (
              <div style={{ color: C.textMuted, fontSize: 11.5, fontWeight: 750, lineHeight: 1.45, marginTop: -8, marginBottom: 18 }}>{plan.note}</div>
            )}
            {leadBusinessDemoPlan ? null : ["coaching", "tournaments"].includes(type) ? (
              <div style={{ display: "flex", justifyContent: "center" }}>
              <button
                type="button"
                className="proam-action"
                onClick={() => type === "coaching" ? startWorkoutCheckout(plan) : startTournamentCheckout(plan)}
                aria-busy={isPlanLoading}
                disabled={plan.comingSoon || isPlanLoading}
                style={{ border: `1px solid ${plan.featured ? "transparent" : C.borderMd}`, background: plan.featured ? C.accentGrad : C.bgElevated, color: plan.featured ? "#fff" : C.text, borderRadius: 12, padding: "13px 18px", fontFamily: "'Sora',sans-serif", fontWeight: 800, cursor: plan.comingSoon ? "not-allowed" : "pointer", opacity: plan.comingSoon ? .65 : 1 }}
              >
                <span>{isPlanLoading ? "Opening Checkout..." : disabledTournamentWebCheckout ? "Open Snows ProAm" : type === "coaching" && coachingBillingCycle === "yearly" && priceKey ? "Subscribe Yearly" : plan.cta}</span>
              </button>
              </div>
            ) : (
              <CTAButton C={C} to={businessDemoPlan ? (institutionBusinessDemoCalendlyUrl || "#book-demo") : target} primary={plan.featured}>
                {type === "wellness" ? businessDemoPlan ? institutionBusinessDemoCalendlyUrl ? "Book a Demo" : "Book a Demo" : plan.metadata?.requires_contact ? "Book a Demo" : "Choose this lane" : plan.cta}
              </CTAButton>
            )}
          </div>
          );
        })}
      </div>
    </section>
  );
}

function FAQ({ C, faqs }) {
  const [open, setOpen] = useState(0);
  if (!faqs) return null;
  return (
    <section className="fu" style={{ maxWidth: 840, margin: "0 auto", padding: "34px 18px" }}>
      <h2 style={{ fontFamily: "'Sora',sans-serif", fontSize: 36, margin: "0 0 16px" }}>FAQ</h2>
      {faqs.map(([q, a], i) => (
        <div key={q} style={{ borderBottom: `1px solid ${C.border}` }}>
          <button onClick={() => setOpen(open === i ? -1 : i)} style={{ width: "100%", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 16, background: "transparent", border: "none", padding: "18px 0", color: C.text, fontWeight: 800, cursor: "pointer", textAlign: "left", fontSize: 16 }}>
            {q}<FontAwesomeIcon icon={faChevronDown} style={{ transform: open === i ? "rotate(180deg)" : "none", transition: ".2s" }} />
          </button>
          {open === i && <p style={{ color: C.textSec, margin: "0 0 18px", lineHeight: 1.6 }}>{a}</p>}
        </div>
      ))}
    </section>
  );
}

function LeadForms() {
  return null;
}

export default function BusinessLandingPage({ type }) {
  const basePage = pages[type] || pages.wellness;
  const [C, toggleTheme, isDark] = useTheme();
  const [institutionBusinessDemoMode, setInstitutionBusinessDemoMode] = useState(false);
  const [institutionBusinessDemoCalendlyUrl, setInstitutionBusinessDemoCalendlyUrl] = useState("");
  const [institutionPublicPricingHidden, setInstitutionPublicPricingHidden] = useState(true);
  const hideInstitutionPricing = type === "wellness" && institutionPublicPricingHidden;
  const page = hideInstitutionPricing
    ? { ...basePage, hero: { ...basePage.hero, primary: "Talk to our team", primaryTo: "#pricing" }, pricing: [] }
    : basePage;

  useEffect(() => {
    if (type !== "wellness") return undefined;
    let mounted = true;
    loadInstitutionBusinessDemoConfig()
      .then((config) => {
        if (mounted) {
          setInstitutionBusinessDemoMode(config.demoMode);
          setInstitutionBusinessDemoCalendlyUrl(config.calendlyUrl);
          setInstitutionPublicPricingHidden(config.publicPricingHidden);
        }
      })
      .catch(() => {
        if (mounted) {
          setInstitutionBusinessDemoMode(false);
          setInstitutionBusinessDemoCalendlyUrl("");
          setInstitutionPublicPricingHidden(true);
        }
      });
    return () => {
      mounted = false;
    };
  }, [type]);

  useEffect(() => {
    const style = document.createElement("style");
    style.textContent = `
      @import url('https://fonts.googleapis.com/css2?family=Sora:wght@600;700;800;900&family=DM+Sans:wght@400;500;600;700&display=swap');
      *,*::before,*::after{box-sizing:border-box} html{scroll-behavior:smooth}
      body{margin:0;font-family:'DM Sans',sans-serif;background:${C.bg};color:${C.text};overflow-x:hidden;}
      @keyframes fadeUp{from{opacity:0;transform:translateY(18px)}to{opacity:1;transform:translateY(0)}} .fu{animation:fadeUp .55s cubic-bezier(.16,1,.3,1) both}
      .proam-motion-card{transition:transform .22s ease,box-shadow .22s ease,border-color .22s ease}
      .proam-motion-card:hover{transform:translateY(-3px)}
      .proam-action:focus-visible,.proam-header-brand:focus-visible,a:focus-visible,button:focus-visible,input:focus-visible,select:focus-visible{outline:3px solid ${C.accent};outline-offset:3px}
      .proam-action:hover{transform:translateY(-1px)}
      .proam-header-brand:hover{transform:none}
      .tournament-gradient-word{background:${C.accentGrad};-webkit-background-clip:text;background-clip:text;-webkit-text-fill-color:transparent;color:${C.accent}}
      .wellness-gradient-word{background:${C.accentGrad};-webkit-background-clip:text;background-clip:text;-webkit-text-fill-color:transparent;color:${C.accent}}
      .events-gradient-word{background:${C.accentGrad};-webkit-background-clip:text;background-clip:text;-webkit-text-fill-color:transparent;color:${C.accent}}
      .coaching-gradient-word{background:${C.accentGrad};-webkit-background-clip:text;background-clip:text;-webkit-text-fill-color:transparent;color:${C.accent}}
      .tournament-cinema-bg{position:absolute;inset:0;pointer-events:none;background:radial-gradient(circle at 76% 18%,rgba(43,127,255,.30),transparent 28%),radial-gradient(circle at 18% 70%,rgba(0,212,255,.10),transparent 28%)}
      .tournament-cinema-hero .tournament-cinema-bg{background-image:linear-gradient(${C.mode === "dark" ? "rgba(255,255,255,.045)" : "rgba(10,29,68,.055)"} 1px,transparent 1px),linear-gradient(90deg,${C.mode === "dark" ? "rgba(255,255,255,.035)" : "rgba(10,29,68,.045)"} 1px,transparent 1px),linear-gradient(120deg,transparent 0%,${C.mode === "dark" ? "rgba(43,127,255,.16)" : "rgba(43,127,255,.10)"} 46%,transparent 72%);background-size:64px 64px,64px 64px,100% 100%;opacity:.8}
      .tournament-motion-field{position:absolute;inset:0;overflow:hidden;pointer-events:none}
      .tournament-trace{position:absolute;height:2px;width:45%;border-radius:999px;background:linear-gradient(90deg,transparent,rgba(43,127,255,.72),rgba(0,212,255,.62),transparent);filter:drop-shadow(0 0 18px rgba(43,127,255,.28));animation:tournamentTrace 8s linear infinite}
      .tournament-trace-one{top:22%;left:-46%;animation-delay:0s}.tournament-trace-two{top:68%;left:-46%;animation-delay:3s}
      .tournament-dashboard-panel{animation:tournamentFloat 6s ease-in-out infinite}.tournament-ticket-card{animation:tournamentTicketFloat 6.4s ease-in-out infinite}
      .tournament-panel-scan{position:absolute;inset:0;background:linear-gradient(110deg,transparent 0%,transparent 38%,${C.mode === "dark" ? "rgba(255,255,255,.06)" : "rgba(43,127,255,.08)"} 48%,transparent 58%,transparent 100%);transform:translateX(-82%);animation:tournamentScan 7s ease-in-out infinite;pointer-events:none}
      .tournament-live-stat,.tournament-control-chip,.tournament-path-card{transition:transform .22s ease,border-color .22s ease,box-shadow .22s ease}
      .tournament-live-stat{animation:tournamentSoftPulse 5.8s ease-in-out infinite}.tournament-live-stat:nth-child(2){animation-delay:.45s}.tournament-live-stat:nth-child(3){animation-delay:.9s}
      .tournament-score-fill,.tournament-ticket-fill{transform-origin:left center;animation:tournamentProgress 4.8s ease-in-out infinite}
      .tournament-live-pill,.tournament-pulse-dot{animation:tournamentPulse 1.8s ease-in-out infinite}
      .tournament-ticket-row{animation:tournamentFeedLift 5s ease-in-out infinite}.tournament-ticket-row:nth-child(2){animation-delay:.2s}.tournament-ticket-row:nth-child(3){animation-delay:.4s}
      .tournament-flow-card::after,.tournament-path-card::before{content:"";position:absolute;left:0;right:0;bottom:0;height:3px;background:linear-gradient(90deg,rgba(43,127,255,.9),rgba(0,212,255,.78),rgba(245,158,11,.72));transform-origin:left center;animation:tournamentRail 4.6s ease-in-out infinite}
      .tournament-path-card::before{top:0;bottom:auto}
      @keyframes tournamentTrace{0%{transform:translateX(0)}100%{transform:translateX(330%)}}
      @keyframes tournamentFloat{0%,100%{transform:translateY(0)}50%{transform:translateY(-8px)}}
      @keyframes tournamentTicketFloat{0%,100%{transform:translateY(0) rotate(-1deg)}50%{transform:translateY(9px) rotate(.4deg)}}
      @keyframes tournamentScan{0%,52%{transform:translateX(-82%)}100%{transform:translateX(90%)}}
      @keyframes tournamentSoftPulse{0%,100%{transform:translateY(0);border-color:${C.border}}50%{transform:translateY(-2px);border-color:${C.accentBorder}}}
      @keyframes tournamentProgress{0%,100%{transform:scaleX(.86)}50%{transform:scaleX(1)}}
      @keyframes tournamentPulse{0%,100%{opacity:1;box-shadow:0 0 0 0 rgba(34,197,94,.22)}50%{opacity:.78;box-shadow:0 0 0 7px rgba(34,197,94,.08)}}
      @keyframes tournamentFeedLift{0%,100%{transform:translateY(0)}50%{transform:translateY(-4px)}}
      @keyframes tournamentRail{0%,100%{transform:scaleX(.28)}50%{transform:scaleX(1)}}
      .tournament-product-preview{isolation:isolate}
      .tournament-laptop-shell{animation:tournamentLaptopFloat 7.2s ease-in-out infinite}
      .tournament-laptop-base{width:74%;height:14px;margin:0 auto;border-radius:0 0 18px 18px;background:linear-gradient(180deg,rgba(10,29,68,.20),rgba(10,29,68,.06));border:1px solid ${C.mode === "dark" ? "rgba(255,255,255,.10)" : "rgba(10,29,68,.08)"};border-top:0}
      .tournament-sync-beam{position:absolute;left:150px;right:96px;bottom:174px;height:3px;border-radius:999px;background:linear-gradient(90deg,transparent,rgba(43,127,255,.76),rgba(0,212,255,.72),transparent);filter:drop-shadow(0 0 16px rgba(43,127,255,.34));transform-origin:left center;animation:tournamentSyncBeam 4.8s ease-in-out infinite;z-index:3}
      .tournament-public-live-dot{display:inline-block;width:7px;height:7px;border-radius:999px;background:#28E07F;box-shadow:0 0 14px rgba(40,224,127,.75);animation:tournamentPulse 1.8s ease-in-out infinite}
      .tournament-bracket-movie{position:relative;margin-top:10px;width:min(410px,72%);min-height:178px;display:grid;grid-template-columns:1.25fr .92fr .74fr;gap:13px}
      .tournament-bracket-round{position:relative;min-width:0}
      .tournament-bracket-round-label{color:rgba(255,255,255,.42);font-size:10px;font-weight:950;letter-spacing:.08em;text-transform:uppercase;margin-bottom:8px}
      .tournament-bracket-node-list{display:grid;gap:8px}
      .tournament-bracket-round-2 .tournament-bracket-node-list{padding-top:28px;gap:32px}
      .tournament-bracket-round-3 .tournament-bracket-node-list{padding-top:76px}
      .tournament-bracket-node{position:relative;display:grid;grid-template-columns:24px minmax(0,1fr) auto;align-items:center;gap:7px;min-height:38px;padding:7px;border-radius:13px;background:rgba(255,255,255,.08);border:1px solid rgba(255,255,255,.13);color:#fff;box-shadow:0 12px 30px rgba(0,0,0,.12);overflow:hidden}
      .tournament-bracket-node::after{content:"";position:absolute;inset:0;background:linear-gradient(110deg,transparent 0%,rgba(255,255,255,.16) 45%,transparent 62%);transform:translateX(-130%);animation:tournamentNodeScan 5.6s ease-in-out infinite}
      .tournament-bracket-node strong{min-width:0;font-size:11px;font-weight:950;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
      .tournament-bracket-node em{font-style:normal;min-width:24px;border-radius:999px;padding:4px 5px;background:rgba(245,184,46,.16);color:#F5B82E;font-size:9px;font-weight:950;text-align:center}
      .tournament-bracket-avatar{width:24px;height:24px;border-radius:9px;background:linear-gradient(135deg,#2B7FFF,#00D4FF);display:grid;place-items:center;font-size:10px;font-weight:950;box-shadow:0 0 0 3px rgba(43,127,255,.12)}
      .tournament-bracket-node-1-1,.tournament-bracket-node-2-1,.tournament-bracket-node-3-1{animation:tournamentNodeAdvance 6.4s ease-in-out infinite}
      .tournament-bracket-node-1-3,.tournament-bracket-node-2-2{animation:tournamentNodeAdvance 6.4s ease-in-out infinite;animation-delay:1.35s}
      .tournament-bracket-connector{position:absolute;height:2px;border-radius:999px;background:linear-gradient(90deg,rgba(126,179,255,.22),rgba(126,179,255,.72));transform-origin:left center;animation:tournamentConnectorLive 6.4s ease-in-out infinite;z-index:0}
      .tournament-bracket-connector-one{left:31%;top:77px;width:16%}
      .tournament-bracket-connector-two{left:62%;top:117px;width:16%;animation-delay:1.2s}
      .tournament-admin-movie-card{animation:tournamentAdminCard 7s ease-in-out infinite;z-index:4}
      .tournament-admin-save-pill{border-radius:999px;padding:6px 8px;background:rgba(34,163,111,.12);border:1px solid rgba(34,163,111,.22);color:#166534;font-size:9px;font-weight:950;text-transform:uppercase;animation:tournamentSaveState 4.8s ease-in-out infinite}
      .tournament-score-entry-row{display:grid;grid-template-columns:minmax(0,1fr) 32px 26px 32px minmax(0,1fr);gap:6px;align-items:center}
      .tournament-score-entry-row strong{display:block;color:#0F172A;font-size:10.5px;font-weight:950;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
      .tournament-score-entry-row span{display:block;margin-top:2px;color:#64748B;font-size:8.5px;font-weight:850;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
      .tournament-score-box{height:32px;border-radius:10px;background:#EEF2F7;border:1px solid #D7E1EE;display:grid;place-items:center;color:#0F172A;font-size:14px;font-weight:950;animation:tournamentScoreBlink 4.8s ease-in-out infinite}
      .tournament-score-box-b{animation-delay:.22s}
      .tournament-score-vs{height:26px;border-radius:9px;background:#EAF4FF;color:${C.accent};display:grid;place-items:center;font-size:8px;font-weight:950}
      .tournament-queue-mini{display:flex;justify-content:space-between;gap:10px;margin-top:10px;border-radius:12px;background:#EAF4FF;border:1px solid #B9DDFB;padding:8px 10px}
      .tournament-queue-mini span{color:#0C69C8;font-size:9px;font-weight:950;text-transform:uppercase}
      .tournament-queue-mini strong{color:#0F172A;font-size:11px;font-weight:950;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
      .tournament-phone-shell{animation:tournamentPhoneMovieFloat 6.8s ease-in-out infinite;z-index:5}
      .tournament-phone-notch{position:absolute;left:50%;top:8px;width:58px;height:5px;border-radius:999px;background:rgba(255,255,255,.24);transform:translateX(-50%);z-index:2}
      .tournament-phone-progress-fill{width:100%;transform-origin:left center;animation:tournamentPhoneProgress 5.2s ease-in-out infinite}
      .tournament-phone-row{animation:tournamentPhoneRow 5.2s ease-in-out infinite}
      .tournament-phone-row:nth-child(2){animation-delay:.22s}.tournament-phone-row:nth-child(3){animation-delay:.44s}
      .tournament-phone-join{margin-top:10px;border-radius:15px;background:linear-gradient(135deg,#1A6FEB,#0A1D44);color:#fff;padding:11px 12px;text-align:center;font-size:12px;font-weight:950;box-shadow:0 12px 26px rgba(26,111,235,.24);animation:tournamentJoinPulse 4.8s ease-in-out infinite}
      .tournament-phone-feed{display:grid;gap:5px;margin-top:9px}
      .tournament-phone-feed span{display:block;border-radius:999px;background:#FFFFFF;border:1px solid #D7E7F8;color:#64748B;padding:5px 8px;font-size:9px;font-weight:900;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;animation:tournamentFeedLift 5s ease-in-out infinite}
      .tournament-phone-feed span:nth-child(2){animation-delay:.3s}.tournament-phone-feed span:nth-child(3){animation-delay:.6s}
      .tournament-movie-step{animation:tournamentMovieStep 6.4s ease-in-out infinite}
      .tournament-movie-step-2{animation-delay:.8s}.tournament-movie-step-3{animation-delay:1.6s}.tournament-movie-step-4{animation-delay:2.4s}
      @keyframes tournamentLaptopFloat{0%,100%{transform:translateY(0) rotate(.2deg)}50%{transform:translateY(-7px) rotate(0deg)}}
      @keyframes tournamentPhoneMovieFloat{0%,100%{transform:translateY(0) rotate(-1.4deg)}50%{transform:translateY(9px) rotate(-.3deg)}}
      @keyframes tournamentSyncBeam{0%,100%{opacity:.38;transform:scaleX(.18)}42%,62%{opacity:1;transform:scaleX(1)}}
      @keyframes tournamentNodeAdvance{0%,100%{border-color:rgba(255,255,255,.13);box-shadow:0 12px 30px rgba(0,0,0,.12)}45%,62%{border-color:rgba(245,184,46,.52);box-shadow:0 0 0 3px rgba(245,184,46,.10),0 16px 38px rgba(0,0,0,.18)}}
      @keyframes tournamentNodeScan{0%,48%{transform:translateX(-130%)}72%,100%{transform:translateX(130%)}}
      @keyframes tournamentConnectorLive{0%,100%{opacity:.32;transform:scaleX(.34)}45%,62%{opacity:1;transform:scaleX(1)}}
      @keyframes tournamentAdminCard{0%,100%{transform:translateY(0)}50%{transform:translateY(-5px)}}
      @keyframes tournamentSaveState{0%,30%,100%{color:#166534;background:rgba(34,163,111,.12)}50%,66%{color:#0C69C8;background:rgba(12,105,200,.10)}}
      @keyframes tournamentScoreBlink{0%,100%{border-color:#D7E1EE;background:#EEF2F7}46%,62%{border-color:#0C69C8;background:#EAF4FF}}
      @keyframes tournamentPhoneProgress{0%,100%{transform:scaleX(.46)}42%,70%{transform:scaleX(.76)}}
      @keyframes tournamentPhoneRow{0%,100%{transform:translateY(0);border-color:#D7E7F8}52%{transform:translateY(-3px);border-color:#B9DDFB}}
      @keyframes tournamentJoinPulse{0%,100%{transform:translateY(0);box-shadow:0 12px 26px rgba(26,111,235,.24)}50%{transform:translateY(-2px);box-shadow:0 18px 32px rgba(26,111,235,.32)}}
      @keyframes tournamentMovieStep{0%,100%{transform:translateY(0);opacity:.7}42%,60%{transform:translateY(-3px);opacity:1}}
	      .tournament-real-live{isolation:isolate;min-height:auto}
	      .tournament-real-topbar{height:44px;padding:0 13px;display:flex;align-items:center;justify-content:space-between;background:rgba(245,247,250,.96);border-bottom:1px solid rgba(10,29,68,.08)}
	      .tournament-real-brand{display:flex;align-items:center;gap:8px;min-width:0}
	      .tournament-real-brand span{width:24px;height:24px;border-radius:8px;background:#fff;border:1px solid rgba(10,29,68,.10);display:grid;place-items:center;color:#0C69C8;font-size:10px;font-weight:950}
	      .tournament-real-brand strong{color:#0F1629;font-size:11px;font-weight:950;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
	      .tournament-real-admin-pill{border-radius:8px;background:#0C69C8;color:#fff;padding:7px 9px;font-size:10px;font-weight:950}
	      .tournament-real-stage{position:relative;padding:18px 18px 12px}
	      .tournament-real-title-block{max-width:360px}
	      .tournament-real-kicker{color:#F5B82E;font-size:10px;font-weight:950;letter-spacing:.18em;text-transform:uppercase}
	      .tournament-real-title{margin-top:7px;color:#fff;font-family:'Sora',sans-serif;font-size:clamp(24px,3.5vw,38px);font-weight:950;line-height:.96;text-transform:uppercase}
	      .tournament-real-meta{margin-top:9px;display:flex;align-items:center;gap:7px;color:rgba(255,255,255,.72);font-size:11px;font-weight:850}
	      .tournament-real-meta span{width:7px;height:7px;border-radius:999px;background:#28E07F;box-shadow:0 0 14px rgba(40,224,127,.75);animation:tournamentPulse 1.8s ease-in-out infinite}
	      .tournament-real-chip-row{margin-top:10px;display:flex;flex-wrap:wrap;gap:6px}
	      .tournament-real-chip-row span{border-radius:999px;border:1px solid rgba(255,255,255,.16);background:rgba(255,255,255,.10);color:rgba(255,255,255,.88);padding:6px 9px;font-size:10px;font-weight:900}
	      .tournament-real-bracket-label{display:flex;align-items:center;gap:8px;margin-top:14px;color:#F5B82E;font-size:11px;font-weight:950;text-transform:uppercase;letter-spacing:.14em}
	      .tournament-real-bracket-label span{width:5px;height:24px;border-radius:999px;background:#F5B82E}
	      .tournament-real-tree-shell{margin-top:10px;border-radius:24px;overflow:hidden;background:rgba(5,18,48,.18);border:1px solid rgba(255,255,255,.10);box-shadow:inset 0 1px 0 rgba(255,255,255,.08),0 24px 80px rgba(0,0,0,.18);backdrop-filter:blur(10px)}
	      .tournament-real-tree-scroll{overflow:hidden;padding:14px}
	      .tournament-real-tree-canvas{position:relative;width:560px;height:395px;transform:scale(.94);transform-origin:top left}
	      .tournament-real-tree-canvas svg{position:absolute;inset:0;width:560px;height:420px}
	      .tournament-real-path{fill:none;stroke:rgba(255,255,255,.14);stroke-width:1.5;stroke-linecap:round}
	      .tournament-real-path-1,.tournament-real-path-2,.tournament-real-path-3,.tournament-real-path-4,.tournament-real-path-5,.tournament-real-path-6,.tournament-real-path-7,.tournament-real-path-8,.tournament-real-path-9,.tournament-real-path-10,.tournament-real-path-11,.tournament-real-path-12,.tournament-real-path-13,.tournament-real-path-14{stroke:rgba(126,179,255,.58);stroke-width:2;stroke-dasharray:220;stroke-dashoffset:220;animation:tournamentRealDraw 6.8s ease-in-out infinite}
	      .tournament-real-path-2{animation-delay:.18s}.tournament-real-path-3{animation-delay:.36s}.tournament-real-path-4{animation-delay:.54s}.tournament-real-path-5{animation-delay:.72s}.tournament-real-path-6{animation-delay:.9s}.tournament-real-path-7{animation-delay:1.08s}.tournament-real-path-8{animation-delay:1.26s}.tournament-real-path-9{animation-delay:1.7s}.tournament-real-path-10{animation-delay:1.88s}.tournament-real-path-11{animation-delay:2.06s}.tournament-real-path-12{animation-delay:2.24s}.tournament-real-path-13{animation-delay:2.85s}.tournament-real-path-14{animation-delay:3.05s}
	      .tournament-real-round-label{position:absolute;color:rgba(255,255,255,.42);font-size:10px;font-weight:950;letter-spacing:.08em;text-transform:uppercase}
	      .tournament-real-node{position:absolute;width:78px;display:flex;flex-direction:column;align-items:center;gap:5px;animation:tournamentRealNode 6.8s ease-in-out infinite}
	      .tournament-real-node.is-start{width:82px}
	      .tournament-real-avatar{position:relative;width:42px;height:42px;border-radius:999px;background:linear-gradient(135deg,#0C69C8,#4AA8FF);border:2px solid rgba(255,255,255,.18);display:grid;place-items:center;color:#fff;font-size:13px;font-weight:950;box-shadow:0 12px 28px rgba(0,0,0,.18);overflow:hidden}
	      .tournament-real-avatar strong{font-size:13px;font-weight:950}
	      .tournament-real-name{max-width:90px;color:rgba(255,255,255,.88);font-size:11px;font-weight:900;line-height:1.08;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
	      .tournament-real-user{max-width:90px;margin-top:-3px;color:rgba(255,255,255,.48);font-size:9px;font-weight:800;line-height:1.05;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
	      .tournament-real-node.is-winner .tournament-real-avatar{background:linear-gradient(135deg,#F5B82E,#0C69C8);box-shadow:0 0 0 5px rgba(245,184,46,.14),0 16px 38px rgba(0,0,0,.22)}
	      .tournament-real-admin{position:relative;width:auto;margin:0 18px 18px;background:#F8FAFC;border:1px solid rgba(215,225,238,.95);border-radius:22px;color:#0F172A;box-shadow:0 18px 50px rgba(10,29,68,.18);overflow:hidden;animation:tournamentAdminCard 7s ease-in-out infinite}
	      .tournament-real-admin-head{min-height:52px;padding:9px 11px;display:flex;align-items:center;gap:10px;background:#fff;border-bottom:1px solid #D7E1EE}
	      .tournament-real-back{width:30px;height:30px;border:1px solid #D7E1EE;background:#fff;border-radius:10px;color:#0A1D44;font-weight:900;display:grid;place-items:center;flex:0 0 auto}
	      .tournament-real-admin-head div{flex:1;min-width:0}
	      .tournament-real-admin-head strong{display:block;color:#0F172A;font-family:'Sora',sans-serif;font-size:14px;font-weight:950;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
	      .tournament-real-admin-head div span{display:block;margin-top:2px;color:#0C69C8;font-size:10px;font-weight:900}
	      .tournament-real-admin-head em{font-style:normal;border:1px solid #D7E1EE;border-radius:10px;padding:7px 9px;color:#0A1D44;font-size:10px;font-weight:900;white-space:nowrap}
	      .tournament-real-admin-tabs{display:grid;grid-template-columns:repeat(6,minmax(0,1fr));gap:3px;margin:8px 10px;padding:3px;background:#fff;border:1px solid #D7E1EE;border-radius:13px}
	      .tournament-real-admin-tabs span{border-radius:9px;padding:7px 3px;color:#64748B;font-size:8px;font-weight:950;text-align:center;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
	      .tournament-real-admin-tabs span.active{background:#0C69C8;color:#fff}
	      .tournament-real-admin-body{display:block;padding:0 10px 10px}
	      .tournament-real-admin-hero{display:none;gap:10px;background:#EAF4FF;border:1px solid #B9DDFB;border-radius:16px;padding:10px;margin-bottom:0}
	      .tournament-real-admin-hero>span{width:34px;height:34px;border-radius:999px;background:#fff;border:1px solid #B9DDFB;color:#0C69C8;display:grid;place-items:center;flex:0 0 auto}
	      .tournament-real-admin-hero small{display:block;color:#0C69C8;font-size:9px;font-weight:950;text-transform:uppercase;letter-spacing:.08em}
	      .tournament-real-admin-hero strong{display:block;margin-top:2px;color:#0F172A;font-family:'Sora',sans-serif;font-size:14px;font-weight:950}
	      .tournament-real-admin-hero p{margin:4px 0 0;color:#64748B;font-size:10.5px;font-weight:750;line-height:1.35}
	      .tournament-real-score-card{background:#fff;border:1px solid #D7E1EE;border-radius:16px;padding:11px}
	      .tournament-real-score-head{display:flex;justify-content:space-between;gap:8px;margin-bottom:10px}
	      .tournament-real-score-head strong{color:#0C69C8;font-size:10px;font-weight:950;text-transform:capitalize}
	      .tournament-real-score-head span{color:#64748B;font-size:10px;font-weight:850;white-space:nowrap}
	      .tournament-real-score-body{display:grid;grid-template-columns:minmax(0,1fr) 34px minmax(0,1fr);gap:8px;align-items:end}
	      .tournament-real-score-player{display:grid;justify-items:center;gap:5px;min-width:0;text-align:center}
	      .tournament-real-admin-avatar{width:34px;height:34px;border-radius:999px;background:#EEF2F7;border:2px solid #D7E1EE;color:#0C69C8;display:grid;place-items:center;font-size:12px;font-weight:950;overflow:hidden}
	      .tournament-real-score-player strong{max-width:100%;color:#0F172A;font-size:11px;font-weight:950;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
	      .tournament-real-score-player label{color:#64748B;font-size:8.5px;font-weight:950;text-transform:uppercase}
	      .tournament-real-score-input{width:54px;height:34px;border:1.5px solid #0C69C8;border-radius:12px;background:#EEF2F7;color:#0F172A;font-family:'Space Mono',monospace;font-size:15px;font-weight:950;display:grid;place-items:center;animation:tournamentScoreBlink 4.8s ease-in-out infinite}
	      .tournament-real-vs{align-self:center;min-width:32px;height:28px;border-radius:999px;border:1px solid #B9DDFB;background:#EAF4FF;color:#0C69C8;display:grid;place-items:center;font-size:10px;font-weight:950}
	      .tournament-real-save{margin-top:10px;border-radius:12px;background:#0C69C8;color:#fff;padding:10px;text-align:center;font-size:11px;font-weight:950;animation:tournamentJoinPulse 4.8s ease-in-out infinite}
	      @keyframes tournamentRealDraw{0%,18%{stroke-dashoffset:220;opacity:.24}46%,75%{stroke-dashoffset:0;opacity:1}100%{stroke-dashoffset:0;opacity:.36}}
	      @keyframes tournamentRealNode{0%,100%{transform:translateY(0)}48%,64%{transform:translateY(-4px)}}
      .wellness-cinema-hero::before{content:"";position:absolute;inset:0;pointer-events:none;background:linear-gradient(120deg,transparent 0%,${C.mode === "dark" ? "rgba(43,127,255,.10)" : "rgba(43,127,255,.07)"} 44%,transparent 72%);opacity:.72}
      .wellness-visual-shell{animation:wellnessFloat 6s ease-in-out infinite}.wellness-phone-preview{animation:wellnessFloatPhone 6.4s ease-in-out infinite}
      .wellness-feed-card{animation:wellnessFeedLift 4.8s ease-in-out infinite}.wellness-live-stat{animation:wellnessSoftPulse 5.8s ease-in-out infinite}.wellness-live-stat:nth-child(2){animation-delay:.4s}.wellness-live-stat:nth-child(3){animation-delay:.8s}.wellness-live-stat:nth-child(4){animation-delay:1.2s}
      .wellness-progress-fill,.wellness-phone-fill{transform-origin:left center;animation:wellnessProgress 4.8s ease-in-out infinite}
      .wellness-pulse-dot,.wellness-live-pill{animation:wellnessPulse 1.8s ease-in-out infinite}
      .wellness-panel-scan{position:absolute;inset:0;background:linear-gradient(110deg,transparent 0%,transparent 38%,${C.mode === "dark" ? "rgba(255,255,255,.06)" : "rgba(43,127,255,.08)"} 48%,transparent 58%,transparent 100%);transform:translateX(-80%);animation:wellnessScan 6.8s ease-in-out infinite;pointer-events:none}
      .wellness-loop-step::after{content:"";position:absolute;left:0;right:0;bottom:0;height:3px;background:linear-gradient(90deg,rgba(43,127,255,.9),rgba(0,169,143,.82),rgba(245,158,11,.72));transform-origin:left center;animation:wellnessRail 4.6s ease-in-out infinite}
      .wellness-loop-pulse{position:absolute;right:12px;top:12px;width:8px;height:8px;border-radius:8px;box-shadow:0 0 0 7px rgba(43,127,255,.10);animation:wellnessPulse 2s ease-in-out infinite}
      .wellness-story-card{display:flex;flex-direction:column}
      .wellness-hero-grid > *{min-width:0}
      @media(max-width:640px){.wellness-hero-grid{grid-template-columns:minmax(0,1fr)!important}.wellness-hero-title>span{white-space:normal!important}.wellness-case-study-action{grid-template-columns:minmax(0,1fr)!important}.wellness-case-study-action>button{width:100%}}
      @keyframes wellnessFloat{0%,100%{transform:translateY(0)}50%{transform:translateY(-8px)}}
      @keyframes wellnessFloatPhone{0%,100%{transform:translateY(0) rotate(-1.5deg)}50%{transform:translateY(9px) rotate(-.5deg)}}
      @keyframes wellnessFeedLift{0%,100%{transform:translateY(0)}50%{transform:translateY(-4px)}}
      @keyframes wellnessSoftPulse{0%,100%{transform:translateY(0);border-color:${C.border}}50%{transform:translateY(-2px);border-color:${C.accentBorder}}}
      @keyframes wellnessProgress{0%,100%{transform:scaleX(.86)}50%{transform:scaleX(1)}}
      @keyframes wellnessPulse{0%,100%{opacity:1;box-shadow:0 0 0 0 rgba(34,197,94,.22)}50%{opacity:.78;box-shadow:0 0 0 7px rgba(34,197,94,.08)}}
      @keyframes wellnessScan{0%,52%{transform:translateX(-80%)}100%{transform:translateX(90%)}}
      @keyframes wellnessRail{0%,100%{transform:scaleX(.28)}50%{transform:scaleX(1)}}
      .wellness-skeleton{position:relative;overflow:hidden}
      .wellness-skeleton::after{content:"";position:absolute;inset:0;transform:translateX(-100%);background:linear-gradient(90deg,transparent,${C.mode === "dark" ? "rgba(255,255,255,.08)" : "rgba(10,29,68,.08)"},transparent);animation:wellnessShimmer 1.25s ease-in-out infinite}
      @keyframes wellnessShimmer{to{transform:translateX(100%)}}
      @media(max-width:1100px){.tournament-cinema-grid,.wellness-hero-grid,.wellness-proof-layout{grid-template-columns:1fr!important}.tournament-hero-copy,.wellness-hero-copy{text-align:left}.tournament-hero-visual,.wellness-hero-visual{max-width:720px;width:100%;margin:0 auto}.tournament-dashboard-panel,.wellness-visual-shell{right:0!important}}
      @media(max-width:980px){.wellness-metric-grid{grid-template-columns:repeat(2,minmax(0,1fr))!important}.wellness-proof-card{grid-template-columns:1fr!important}.wellness-proof-metrics{grid-template-columns:repeat(2,minmax(0,1fr))!important}.wellness-loop{grid-template-columns:repeat(2,minmax(0,1fr))!important}}
      @media(max-width:860px){.tournament-cinema-grid{grid-template-columns:1fr!important}.tournament-hero-proof{grid-template-columns:1fr!important}}
      @media(max-width:760px){.biz-grid,.price-grid,.institution-demo-grid,.institution-demo-form-grid,.business-demo-feature-grid{grid-template-columns:1fr!important}.biz-nav{max-width:calc(100vw - 92px)}.phone-wrap{min-height:430px!important;margin-top:6px} #event-form form{grid-template-columns:1fr!important}}
      @media(max-width:760px){.relay-proof-metrics{grid-template-columns:repeat(2,minmax(0,1fr))!important}}
      @media(max-width:640px){.biz-header-center{display:none!important}.biz-header-actions{gap:6px!important}.tournament-flow-title{white-space:normal!important}.tournament-cinema-hero,.wellness-cinema-hero,.events-cinema-hero,.coaching-cinema-hero{padding:30px 16px 32px!important}.tournament-hero-title,.events-hero-title,.coaching-hero-title{font-size:clamp(36px,11.5vw,52px)!important;line-height:.95!important;margin-bottom:14px!important;max-width:100%!important;overflow-wrap:normal!important}.wellness-hero-title{font-size:clamp(28px,8.6vw,40px)!important;line-height:1.04!important;margin-bottom:14px!important;max-width:100%!important;overflow-wrap:normal!important}.tournament-hero-actions,.wellness-hero-actions,.events-hero-actions,.coaching-hero-actions{display:grid!important;grid-template-columns:1fr!important;max-width:360px}.tournament-hero-actions button,.wellness-hero-actions button,.events-hero-actions button,.coaching-hero-actions button{width:100%}.wellness-trust-line,.events-trust-line,.coaching-trust-line{font-size:12.5px!important;line-height:1.45!important;max-width:320px!important}.tournament-hero-visual,.wellness-hero-visual{min-height:auto!important;display:grid;gap:12px}.tournament-dashboard-panel,.tournament-ticket-card,.wellness-visual-shell,.wellness-phone-preview{position:relative!important;inset:auto!important;left:auto!important;right:auto!important;top:auto!important;width:100%!important;min-height:auto!important}.tournament-dashboard-panel,.wellness-visual-shell{padding:14px!important}.tournament-ticket-card,.wellness-phone-preview{max-width:300px;margin:0 auto!important}.wellness-visual-grid,.wellness-audience-pills{grid-template-columns:1fr!important}.wellness-audience-pills{max-width:360px}}
      @media(max-width:640px){.tournament-hero-proof{display:none!important}.tournament-product-preview{min-height:auto!important;display:grid!important;gap:14px}.tournament-sync-beam,.tournament-movie-rail,.tournament-bracket-connector{display:none!important}.tournament-laptop-shell,.tournament-phone-shell{position:relative!important;inset:auto!important;left:auto!important;right:auto!important;top:auto!important;bottom:auto!important;width:100%!important}.tournament-laptop-shell{order:2}.tournament-phone-shell{order:1;max-width:260px;margin:0 auto!important}.tournament-laptop-screen{min-height:auto!important}.tournament-laptop-stage{padding:18px 14px 14px!important}.tournament-bracket-movie{width:100%!important;min-height:auto!important;grid-template-columns:1fr!important;gap:8px}.tournament-bracket-round-2 .tournament-bracket-node-list,.tournament-bracket-round-3 .tournament-bracket-node-list{padding-top:0!important;gap:8px}.tournament-admin-movie-card{position:relative!important;right:auto!important;bottom:auto!important;width:100%!important;margin-top:12px;box-shadow:none!important}.tournament-laptop-base{display:none!important}.tournament-score-entry-row{grid-template-columns:minmax(0,1fr) 30px 24px 30px minmax(0,1fr)!important}.tournament-laptop-top-actions span:first-child{display:none!important}}
	      @media(max-width:1180px) and (min-width:641px){.tournament-real-tree-canvas{transform:scale(.9);height:378px!important}.tournament-real-admin{width:auto!important}}
	      @media(max-width:640px){.tournament-real-live{min-height:auto!important}.tournament-real-stage{padding:16px 12px 12px!important}.tournament-real-title{font-size:28px!important}.tournament-real-tree-scroll{overflow:hidden!important;padding:10px!important}.tournament-real-tree-canvas{transform:scale(.58)!important;height:244px!important;width:560px!important}.tournament-real-admin{position:relative!important;right:auto!important;bottom:auto!important;width:auto!important;margin:0 12px 12px!important;box-shadow:none!important}.tournament-real-admin-body{grid-template-columns:1fr!important}.tournament-real-admin-hero{display:none!important}.tournament-real-admin-tabs span{font-size:7.5px!important}.tournament-real-score-body{grid-template-columns:minmax(0,1fr) 30px minmax(0,1fr)!important}}
	      @media(max-width:420px){.tournament-real-tree-canvas{transform:scale(.52)!important;height:219px!important}.tournament-real-admin-head em{display:none!important}}
      @media(max-width:520px){.tournament-hero-proof{grid-template-columns:1fr!important}.tournament-live-board > div:last-child{grid-template-columns:1fr!important}.tournament-match-row{grid-template-columns:54px minmax(0,1fr)!important}.tournament-match-row > div:last-child{text-align:left!important}.tournament-dashboard-panel > div:nth-child(3){grid-template-columns:1fr!important}}
      @media(max-width:420px){.wellness-metric-grid{grid-template-columns:1fr!important}.wellness-hero-title{font-size:clamp(34px,10.8vw,42px)!important}.wellness-phone-preview{max-width:238px!important}.wellness-live-row{grid-template-columns:36px minmax(0,1fr)!important}.wellness-live-row > div:first-child{width:36px!important;height:36px!important}}
      @media(prefers-reduced-motion:reduce){html{scroll-behavior:auto}*,*::before,*::after{animation:none!important;transition:none!important}.proam-motion-card:hover,.proam-action:hover,.proam-header-brand:hover{transform:none!important}}
    `;
    document.head.appendChild(style);
    return () => style.remove();
  }, [C]);

  const isCoaching = type === "coaching";
  const Icon = page.icon || faBuilding;
  return (
    <div style={{ minHeight: "100vh", background: C.bg, color: C.text }}>
      <Header C={C} isDark={isDark} toggleTheme={toggleTheme} page={page} />
      <main>
        {type === "tournaments" ? (
          <TournamentHero C={C} page={page} />
        ) : type === "wellness" ? (
          <WellnessHero C={C} page={page} />
        ) : type === "events" ? (
          <EventsHero C={C} page={page} />
        ) : type === "coaching" ? (
          <CoachingHero C={C} page={page} />
        ) : (
          <section style={{ position: "relative", overflow: "hidden", padding: isCoaching ? "72px 18px 46px" : "92px 18px 58px", background: `linear-gradient(180deg,${C.accentBg},transparent)` }}>
            <div style={{ maxWidth: 1080, margin: "0 auto", display: "grid", gridTemplateColumns: isCoaching ? "1fr 1fr" : "1.2fr .8fr", gap: isCoaching ? 18 : 24, alignItems: "center" }} className="biz-grid">
              <div className="fu">
                <div style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: "6px 11px", borderRadius: 999, background: C.accentBg, border: `1px solid ${C.accentBorder}`, color: C.accent, fontSize: 11, fontWeight: 900, textTransform: "uppercase", letterSpacing: .5, marginBottom: isCoaching ? 14 : 18 }}>
                  <FontAwesomeIcon icon={Icon} /> ProAm for {page.nav}
                </div>
                <h1 style={{ fontFamily: "'Sora',sans-serif", fontSize: isCoaching ? "clamp(30px,4.8vw,54px)" : "clamp(40px,7vw,78px)", lineHeight: isCoaching ? 1.04 : .93, letterSpacing: 0, margin: isCoaching ? "0 0 14px" : "0 0 18px" }}>
                  {page.hero.headline}
                </h1>
                <p style={{ color: C.textSec, fontSize: isCoaching ? 16 : 18, lineHeight: isCoaching ? 1.58 : 1.65, maxWidth: isCoaching ? 620 : 740, margin: isCoaching ? "0 0 20px" : "0 0 26px" }}>{page.hero.sub}</p>
                <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
                  <CTAButton C={C} to={page.hero.primaryTo} primary>{page.hero.primary}</CTAButton>
                  <CTAButton C={C} to={page.hero.secondaryTo}>{page.hero.secondary}</CTAButton>
                </div>
              </div>
              <PhoneMockup C={C} isDark={isDark} type={type} />
            </div>
          </section>
        )}

        {type === "tournaments" && (
          <>
            <TournamentServiceGrid C={C} />
            <TournamentOpsStrip C={C} />
          </>
        )}

        {isCoaching && <CoachingJourneyStrip C={C} />}

        {type === "events" ? (
          <EventsCompactExplainer C={C} />
        ) : page.proof && (
          <section className="fu" style={{ maxWidth: 1080, margin: "0 auto", padding: "26px 18px" }}>
            {type === "wellness" ? (
              <article style={{ position: "relative", overflow: "hidden", borderRadius: 24, border: "1px solid rgba(139,92,246,.44)", background: "linear-gradient(135deg,#07070B 0%,#27104E 48%,#6D28D9 100%)", boxShadow: "0 24px 70px rgba(76,29,149,.28)", padding: "clamp(18px,3vw,30px)", color: "#fff" }}>
                <div aria-hidden="true" style={{ position: "absolute", inset: 0, background: "radial-gradient(circle at 8% 8%,rgba(124,58,237,.50),transparent 24%),radial-gradient(circle at 86% 18%,rgba(245,158,11,.20),transparent 22%),radial-gradient(circle at 90% 92%,rgba(139,92,246,.22),transparent 24%)", pointerEvents: "none" }} />
                <div style={{ position: "relative", display: "grid", gridTemplateColumns: "minmax(0,.82fr) minmax(320px,1.18fr)", gap: 18, alignItems: "stretch" }} className="biz-grid">
                  <div style={{ display: "flex", flexDirection: "column", justifyContent: "center" }}>
                    <div style={{ display: "inline-flex", width: "fit-content", alignItems: "center", gap: 8, border: "1px solid rgba(251,191,36,.42)", background: "rgba(251,191,36,.13)", borderRadius: 999, padding: "8px 12px", color: "#FBBF24", fontSize: 11, fontWeight: 950, textTransform: "uppercase", letterSpacing: .55, marginBottom: 14 }}>
                      Event complete
                    </div>
                    <h2 style={{ fontFamily: "'Sora',sans-serif", fontSize: "clamp(30px,4.4vw,50px)", lineHeight: .96, margin: "0 0 8px" }}>Relay for Life</h2>
                    <div style={{ color: "rgba(255,255,255,.88)", fontFamily: "'Sora',sans-serif", fontSize: "clamp(17px,2.3vw,26px)", fontWeight: 950, marginBottom: 10 }}>Steps Challenge 2026</div>
                    <div style={{ color: "rgba(255,255,255,.62)", fontSize: 13, fontWeight: 950, textTransform: "uppercase", letterSpacing: .8, marginBottom: 18 }}>
                      Walk <span style={{ color: "rgba(255,255,255,.34)" }}>·</span> Track <span style={{ color: "rgba(255,255,255,.34)" }}>·</span> <span style={{ color: "#FBBF24" }}>Compete</span>
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(2,minmax(0,1fr))", gap: 10, maxWidth: 440 }}>
                      {[
                        ["Team join code", "2026"],
                        ["Members join code", "12345"],
                      ].map(([label, value]) => (
                        <div key={label} style={{ border: "2px solid rgba(251,191,36,.78)", background: "rgba(29,10,60,.62)", borderRadius: 18, padding: "13px 15px", boxShadow: "0 16px 34px rgba(15,6,32,.20)" }}>
                          <div style={{ color: "#F59E0B", fontSize: 11, fontWeight: 950, textTransform: "uppercase", letterSpacing: .5 }}>{label}</div>
                          <div style={{ marginTop: 4, color: "#fff", fontFamily: "'Sora',sans-serif", fontSize: "clamp(22px,3vw,34px)", fontWeight: 950, lineHeight: 1 }}>{value}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div style={{ display: "grid", gap: 12 }}>
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(4,minmax(0,1fr))", gap: 10 }} className="relay-proof-metrics">
                      {[
                        ["5.41M", "Total steps", "rgba(139,92,246,.24)", "rgba(139,92,246,.38)"],
                        ["293", "Athletes", "rgba(59,130,246,.20)", "rgba(59,130,246,.36)"],
                        ["64", "Teams", "rgba(245,158,11,.22)", "rgba(251,191,36,.46)"],
                        ["4,120 km", "Covered", "rgba(16,185,129,.17)", "rgba(16,185,129,.32)"],
                      ].map(([value, label, bg, border], index) => (
                        <div key={label} style={{ minWidth: 0, minHeight: 118, borderRadius: 18, border: `1px solid ${border}`, background: `linear-gradient(145deg,${bg},rgba(255,255,255,.055))`, padding: "13px 11px", display: "grid", alignContent: "end", overflow: "hidden", boxShadow: "inset 0 1px 0 rgba(255,255,255,.08)" }}>
                          <div style={{ minWidth: 0, color: index === 2 ? "#FBBF24" : "#fff", fontFamily: "'Sora',sans-serif", fontSize: value.includes(" ") ? "clamp(13px,1.35vw,21px)" : "clamp(17px,1.8vw,26px)", fontWeight: 950, lineHeight: 1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "clip" }}>{value}</div>
                          <div style={{ color: "rgba(255,255,255,.64)", fontSize: 10.5, fontWeight: 950, textTransform: "uppercase", letterSpacing: .5, marginTop: 8 }}>{label}</div>
                        </div>
                      ))}
                    </div>
                    <div className="wellness-case-study-action" style={{ borderRadius: 20, border: "1px solid rgba(139,92,246,.50)", background: "rgba(7,7,11,.70)", padding: 16, display: "grid", gridTemplateColumns: "minmax(0,1fr) auto", alignItems: "center", gap: 14 }}>
                      <div>
                        <div style={{ color: "#A855F7", fontSize: 11, fontWeight: 950, textTransform: "uppercase", letterSpacing: .6 }}>Case study</div>
                        <div style={{ marginTop: 5, color: "#FBBF24", fontFamily: "'Sora',sans-serif", fontSize: "clamp(22px,3vw,34px)", fontWeight: 950, lineHeight: 1 }}>Finished</div>
                        <div style={{ marginTop: 7, color: "rgba(255,255,255,.72)", fontSize: 12.5, fontWeight: 850, lineHeight: 1.45 }}>Preview the challenge dashboard users see after they join.</div>
                      </div>
                      <CTAButton C={{ ...C, accentGrad: "linear-gradient(135deg,#F59E0B,#7C3AED)", accentGlow: "rgba(245,158,11,.26)", text: "#fff", borderMd: "rgba(255,255,255,.22)" }} to="/relayforlife" primary>
                        View Relay Dashboard
                      </CTAButton>
                    </div>
                  </div>
                </div>
              </article>
            ) : (
              <div className="biz-grid" style={{ display: "grid", gridTemplateColumns: page.globalProof ? "1fr 1fr" : "1fr", gap: 14 }}>
                {[page.proof, page.globalProof].filter(Boolean).map((proof) => (
                  <div key={proof.title} style={{ background: C.bgCard, border: `1px solid ${C.border}`, borderRadius: 18, padding: 22, display: "flex", gap: 18, alignItems: "center", justifyContent: "space-between", flexWrap: "wrap" }}>
                    <div>
                      <h2 style={{ fontFamily: "'Sora',sans-serif", margin: "0 0 10px" }}>{proof.title}</h2>
                      <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                        {proof.stats.map((s) => <span key={s} style={{ color: C.accent, fontWeight: 900 }}>{s}</span>)}
                      </div>
                    </div>
                    <CTAButton C={C} to={proof.to}>{proof.link}</CTAButton>
                  </div>
                ))}
              </div>
            )}
          </section>
        )}

        {isCoaching ? (
          <>
            <CoachingValueStrip C={C} />
            <CoachingShowcase C={C} sections={page.sections} />
          </>
        ) : type === "wellness" ? (
          <WellnessValueStrip C={C} />
        ) : type === "tournaments" ? (
          <TournamentValueStrip C={C} />
        ) : type === "events" ? (
          null
        ) : (
          page.sections.map((section) => <CardGrid key={section.title} C={C} section={section} />)
        )}
        {page.pricing?.length > 0 && (
          <Pricing C={C} plans={page.pricing} target="#trial" type={type} institutionBusinessDemoMode={institutionBusinessDemoMode} institutionBusinessDemoCalendlyUrl={institutionBusinessDemoCalendlyUrl} />
        )}
        {hideInstitutionPricing && (
          <section id="pricing" className="fu proam-modern-surface" style={{ maxWidth: 900, margin: '0 auto', padding: '42px 18px', textAlign: 'center' }}>
            <h2 style={{ fontFamily: "'Sora',sans-serif", fontSize: 'clamp(28px,5vw,44px)', margin: '0 0 12px' }}>Bring Snows ProAm to your organisation</h2>
            <p style={{ color: C.textSec, lineHeight: 1.6, margin: '0 auto 20px', maxWidth: 640 }}>Tell us about your school, club or company. Our team will help you choose the right setup.</p>
            <CTAButton C={C} to={institutionBusinessDemoCalendlyUrl || '/support'} primary>{institutionBusinessDemoCalendlyUrl ? 'Book a conversation' : 'Contact our team'}</CTAButton>
          </section>
        )}
        <FAQ C={C} faqs={page.faqs} />
        <LeadForms C={C} type={page.form} />

        {page.cta && (
          <section id="trial" className="fu" style={{ maxWidth: 900, margin: "0 auto", padding: "42px 18px 84px", textAlign: "center" }}>
            <h2 style={{ fontFamily: "'Sora',sans-serif", fontSize: "clamp(30px,5vw,52px)", margin: "0 0 18px" }}>{page.cta[0]}</h2>
            <CTAButton C={C} to="https://linktr.ee/snowsproam" primary>{page.cta[1]}</CTAButton>
          </section>
        )}
      </main>
    </div>
  );
}

export const coachingPlans = pages.coaching.pricing;
export const coachingFaqs = pages.coaching.faqs;
export function WellnessPricing({ C, ...props }) { return <Pricing C={C} plans={pages.wellness.pricing} target="/institutions/create" type="wellness" {...props} />; }
